import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API = '/api';
const fmt = n => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);
const getAuth = () => ({});

const S = {
  btn: v => ({
    padding: '9px 18px', borderRadius: 6, border: 'none', cursor: 'pointer',
    fontWeight: 700, fontSize: 13, letterSpacing: 0.5, fontFamily: 'inherit',
    background:
      v==='gold' ? 'linear-gradient(135deg, #b8860b, #d4a017)' :
      v==='teal' ? 'linear-gradient(135deg, #2c7873, #4a9e99)' :
      v==='red'  ? 'linear-gradient(135deg, #922b21, #c0392b)' :
                   '#f0ede8',
    color: (v==='gold'||v==='teal'||v==='red') ? '#fff' : '#555',
    boxShadow:
      v==='gold' ? '0 2px 8px rgba(184,134,11,0.3)' :
      v==='teal' ? '0 2px 8px rgba(44,120,115,0.3)' :
      v==='red'  ? '0 2px 8px rgba(192,57,43,0.3)' : 'none',
    transition: 'opacity 0.2s',
  }),
  inp: {
    width: '100%', padding: '9px 12px', borderRadius: 6,
    border: '1px solid #d5c9a8', fontSize: 13,
    background: '#fefcf8', boxSizing: 'border-box',
    fontFamily: 'inherit', color: '#333',
    outline: 'none',
  },
  lbl: { fontSize: 11, color: '#999', marginBottom: 4, display: 'block', letterSpacing: 1, textTransform: 'uppercase' },
  card: {
    background: '#fff', borderRadius: 10,
    border: '1px solid #e8e0cc',
    boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
    padding: 14, marginBottom: 10,
  },
  tag: c => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: 20,
    fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
    background: c==='green'?'#e8f5ee': c==='warn'?'#fef3e2': '#fdecea',
    color: c==='green'?'#1a7a4a': c==='warn'?'#b7600a': '#b71c1c',
  }),
};

const EMPTY_PERFUME = { nombre:'', marca:'', precio:'', costo:'', stock:'', ml:'100', familia:'Femenino', notas:'', origen:'', imagen_url:'', imagen_url_2:'', imagen_url_3:'', inspiracion:{}, precio_decant_5ml:'', precio_decant_10ml:'', imagen_decant:'', tipo_venta:'perfume' };

function EventoBannerPanel({ ev, getAuth, showToast, API }) {
  const [archivos, setArchivos] = React.useState([]);
  const [subiendo, setSubiendo] = React.useState(false);
  const [abierto, setAbierto] = React.useState(false);

  const cargar = async () => {
    try {
      const r = await fetch('/api/upload/event-media/' + ev.key, { headers: getAuth().headers });
      const data = await r.json();
      setArchivos(data);
    } catch {}
  };

  React.useEffect(() => { if (abierto) cargar(); }, [abierto]);

  const subir = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setSubiendo(true);
    for (const file of files) {
      // Validar tamaño
      const isVideo = file.type.includes('video') || /\.(mp4|webm|mov)$/i.test(file.name);
      const maxSize = isVideo ? 150 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) {
        showToast(`${file.name} supera el límite (${isVideo ? '150MB' : '5MB'})`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      try {
        const r = await fetch('/api/upload/event-media/' + ev.key, {
          method: 'POST', headers: getAuth().headers, body: fd,
        });
        if (!r.ok) { showToast('Error subiendo ' + file.name); continue; }
      } catch { showToast('Error subiendo ' + file.name); }
    }
    setSubiendo(false);
    cargar();
    showToast('Archivos subidos');
  };

  const eliminar = async (filename) => {
    if (!window.confirm('¿Eliminar ' + filename + '?')) return;
    await fetch('/api/upload/event-media/' + filename, { method: 'DELETE', headers: getAuth().headers });
    cargar();
    showToast('Archivo eliminado');
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 2, marginBottom: 8, overflow: 'hidden' }}>
      <div
        onClick={() => setAbierto(!abierto)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#111' }}>{ev.label}</span>
          {archivos.length > 0 && (
            <span style={{ fontSize: 10, background: '#f0f0ec', color: '#888', padding: '2px 8px', borderRadius: 2 }}>
              {archivos.length} archivo{archivos.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <a href={'/?preview=' + ev.key} target="_blank" rel="noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ fontSize: 10, color: '#c9a96e', textDecoration: 'none', letterSpacing: 0.5 }}>
            Preview ↗
          </a>
          <span style={{ color: '#bbb', fontSize: 14 }}>{abierto ? '▲' : '▼'}</span>
        </div>
      </div>

      {abierto && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid #f5f5f5' }}>

          {/* GALERÍA */}
          {archivos.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginTop: 12, marginBottom: 12 }}>
              {archivos.map((f, i) => (
                <div key={f.filename} style={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid #ececec', background: '#f9f8f6' }}>
                  <div style={{ position: 'absolute', top: 4, left: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 2, letterSpacing: 1 }}>
                    {i === 0 ? '1° principal' : `${i + 1}°`}
                  </div>
                  <div style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 9, padding: '2px 5px', borderRadius: 2 }}>
                    {f.type === 'video' ? '▶ video' : '🖼 img'}
                  </div>
                  {f.type === 'video' ? (
                    <video src={f.url} style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} muted />
                  ) : (
                    <img src={f.url} alt={f.filename} style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
                  )}
                  <div style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 9, color: '#bbb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 90 }}>{f.filename}</span>
                    <button
                      onClick={() => eliminar(f.filename)}
                      style={{ background: 'none', border: 'none', color: '#E24B4A', cursor: 'pointer', fontSize: 13, padding: '0 2px', lineHeight: 1 }}>
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {archivos.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#bbb', fontSize: 12 }}>Sin archivos para este evento</div>
          )}

          {/* BOTÓN SUBIR */}
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '9px 16px', background: subiendo ? '#f0f0f0' : '#111',
            color: subiendo ? '#aaa' : '#fff',
            borderRadius: 2, fontSize: 11, fontWeight: 600,
            cursor: subiendo ? 'not-allowed' : 'pointer',
            letterSpacing: 1, textTransform: 'uppercase',
          }}>
            {subiendo ? 'Subiendo...' : '+ Agregar video o imagen'}
            <input
              type="file"
              accept="video/mp4,video/webm,video/mov,image/jpeg,image/png,image/webp"
              multiple
              style={{ display: 'none' }}
              disabled={subiendo}
              onChange={subir}
            />
          </label>
          <span style={{ fontSize: 10, color: '#bbb', marginLeft: 10 }}>MP4 · JPG · PNG · WebP</span>
        </div>
      )}
    </div>
  );
}

function VentaRow({ v, editandoFormaPago, setEditandoFormaPago, nuevaFormaPago, setNuevaFormaPago, guardarFormaPago, imprimirRecibo, setConfirmDelete, payingVenta, setPayingVenta, payAmount, setPayAmount, payMethod, setPayMethod, registrarPago, fmt, S, noMargin }) {
  return (
    <div style={{ background: noMargin ? '#f9f8f6' : '#fff', border: '1px solid #ececec', borderRadius: 2, padding: '12px 14px', marginBottom: noMargin ? 0 : 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: '#111' }}>{v.cliente_nombre}</span>
          <span style={{ fontSize: 10, color: '#bbb' }}>{v.fecha?.split('T')[0]}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {editandoFormaPago === v.id ? (
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
              <select
                value={nuevaFormaPago}
                onChange={e => setNuevaFormaPago(e.target.value)}
                style={{ ...S.inp, width: 'auto', padding: '3px 8px', fontSize: 11 }}
                autoFocus
              >
                {['Efectivo','Transferencia','Cuenta corriente','Tarjeta'].map(m => (
                  <option key={m}>{m}</option>
                ))}
              </select>
              <button onClick={() => guardarFormaPago(v.id)} style={{ ...S.btn('gold'), padding: '4px 10px', fontSize: 11 }}>✓</button>
              <button onClick={() => setEditandoFormaPago(null)} style={{ ...S.btn(''), padding: '4px 8px', fontSize: 11 }}>✕</button>
            </div>
          ) : (
            <span
              onClick={() => { setEditandoFormaPago(v.id); setNuevaFormaPago(v.forma_pago); }}
              title="Click para editar forma de pago"
              style={{ fontSize: 11, padding: '2px 8px', borderRadius: 2, cursor: 'pointer', fontWeight: 600, border: '1px solid',
                background: v.forma_pago==='Efectivo'?'#EAF3DE': v.forma_pago==='Transferencia'?'#E6F1FB': v.forma_pago==='Tarjeta'?'#F3F0FA':'#FAEEDA',
                color: v.forma_pago==='Efectivo'?'#3B6D11': v.forma_pago==='Transferencia'?'#0C447C': v.forma_pago==='Tarjeta'?'#3C3489':'#854F0B',
                borderColor: v.forma_pago==='Efectivo'?'#C0DD97': v.forma_pago==='Transferencia'?'#B5D4F4': v.forma_pago==='Tarjeta'?'#AFA9EC':'#FAC775',
              }}>
              {v.forma_pago} ✎
            </span>
          )}
          <span style={{ fontWeight: 600, color: '#111', fontSize: 14, fontFamily: "'Cormorant Garamond',serif" }}>{fmt(v.total)}</span>
          <button onClick={() => imprimirRecibo(v)} title="Imprimir recibo" style={{ background:'none', border:'1px solid #e0e0e0', borderRadius:2, color:'#888', cursor:'pointer', padding:'3px 8px', fontSize:11 }}>🖨</button>
          <button onClick={() => setConfirmDelete({ id: v.id, desc: `${v.cliente_nombre} · ${fmt(v.total)} · ${v.fecha?.split('T')[0]}` })} style={{ background:'none', border:'1px solid #ffcdd2', borderRadius:2, color:'#e53935', cursor:'pointer', padding:'3px 7px', fontSize:11 }}>✕</button>
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#888' }}>
        {v.items?.filter(i => i.nombre).map(i => `${i.nombre}${i.cantidad > 1 ? ' ×'+i.cantidad : ''}`).join(' · ') || '—'}
      </div>
      {parseFloat(v.saldo) > 0 && (
        <div style={{ marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
            <span style={{ fontSize: 11, color: '#e53935', fontWeight: 600 }}>
              Saldo pendiente: {fmt(v.saldo)}
            </span>
            {payingVenta !== v.id && (
              <button onClick={() => { setPayingVenta(v.id); setPayAmount(''); setPayMethod('Efectivo'); }}
                style={{ ...S.btn('teal'), padding: '3px 10px', fontSize: 11 }}>
                Registrar pago
              </button>
            )}
          </div>
          {payingVenta === v.id && (
            <div style={{ marginTop: 8, background: '#f9f8f6', borderRadius: 2, padding: 10, border: '1px solid #ececec' }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input type="number" style={{ ...S.inp, flex: 1 }} placeholder="Monto $" value={payAmount} onChange={e => setPayAmount(e.target.value)} autoFocus />
                <button style={S.btn('gold')} onClick={registrarPago}>Confirmar</button>
                <button style={S.btn('')} onClick={() => setPayingVenta(null)}>✕</button>
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                {['Efectivo','Transferencia','Tarjeta','Cuenta corriente'].map(m => (
                  <button key={m} onClick={() => setPayMethod(m)}
                    style={{ padding: '4px 10px', borderRadius: 2, border: '1px solid', borderColor: payMethod===m?'#111':'#e0e0e0', cursor: 'pointer', fontSize: 11, background: payMethod===m?'#111':'transparent', color: payMethod===m?'#fff':'#888' }}>
                    {m}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button style={{ fontSize: 11, padding: '4px 10px', borderRadius: 2, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer', color: '#888' }} onClick={() => setPayAmount(String(v.saldo))}>Pago total</button>
                <button style={{ fontSize: 11, padding: '4px 10px', borderRadius: 2, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer', color: '#888' }} onClick={() => setPayAmount(String(Math.round(v.saldo/2)))}>Mitad</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [editandoFotoUrls, setEditandoFotoUrls] = useState({});
  const [tab, setTab] = useState('dashboard');
  const [subTab, setSubTab] = useState('listado');
  const [perfumes, setPerfumes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [ventas, setVentas] = useState([]);
  const [openCliente, setOpenCliente] = useState(null);
  const [payingVenta, setPayingVenta] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [selProds, setSelProds] = useState([]);
  const [ventaForm, setVentaForm] = useState({ clienteId: '', paid: '', method: 'Efectivo', date: new Date().toISOString().split('T')[0] });
  const [ventaSearch, setVentaSearch] = useState('');
  const [ventaHistSearch, setVentaHistSearch] = useState('');
  const [openVentaCliente, setOpenVentaCliente] = useState(null);
  const [extrasVenta, setExtrasVenta] = useState([]); // [{id, desc, precio}]
  const [ventaFiltro, setVentaFiltro] = useState('Todos');
  const [editandoPrecio, setEditandoPrecio] = useState(null);
  const [nuevoPrecio, setNuevoPrecio] = useState('');
  const [editandoDescuento, setEditandoDescuento] = useState(null);
  const [nuevoDescuento, setNuevoDescuento] = useState(0);
  const [editandoCosto, setEditandoCosto] = useState(null);
  const [nuevoCosto, setNuevoCosto] = useState('');
  const [editandoFoto, setEditandoFoto] = useState(null);
  const [nuevaFoto, setNuevaFoto] = useState('');
  const [editandoPerfume, setEditandoPerfume] = useState(null);
  const [editPerfumeForm, setEditPerfumeForm] = useState({});
  const [nuevoPerfumeForm, setNuevoPerfumeForm] = useState(EMPTY_PERFUME);
  const [nuevoClienteForm, setNuevoClienteForm] = useState({ nombre: '', telefono: '', email: '', dni: '' });
  const [mostrarNuevoCliente, setMostrarNuevoCliente] = useState(false);
  const [stockSearch, setStockSearch] = useState('');
  const [precioSearch, setPrecioSearch] = useState('');
  const [clienteSearch, setClienteSearch] = useState('');
  const [clienteFechaDesde, setClienteFechaDesde] = useState('');
  const [clienteFechaHasta, setClienteFechaHasta] = useState('');
  const [clientePerfumeFiltro, setClientePerfumeFiltro] = useState('');
  const [clienteDeudaMin, setClienteDeudaMin] = useState('');
  const [clienteSoloDeuda, setClienteSoloDeuda] = useState(false);
  const [clienteInactivosDias, setClienteInactivosDias] = useState('');
  const [clienteFiltrosAbiertos, setClienteFiltrosAbiertos] = useState(false);
  const [clienteFormaPago, setClienteFormaPago] = useState('');
  const [bannerStatus, setBannerStatus] = useState({});
  const [dashData, setDashData]     = useState(null);
  const [visitas, setVisitas] = React.useState(null);
  const [online, setOnline] = React.useState(null);
  const [visitasDiarias, setVisitasDiarias] = React.useState([]);
  const [dashLoading, setDashLoading] = useState(false);
  const [listadoBusq, setListadoBusq] = useState('');
  const [listadoFilt, setListadoFilt] = useState('todos');
  const [provConfig, setProvConfig] = useState(() => {
    try { return JSON.parse(localStorage.getItem('prov_config') || '{}'); } catch { return {}; }
  });
  const [proveedores, setProveedores] = useState([]);
  const [pedidosProv, setPedidosProv] = useState([]);
  const [pedidoForm, setPedidoForm] = useState({
    provNombre: '', moneda: 'USD', tc: '', envioIndiv: '', envioPeso: '',
    items: [{ nombre: '', cantidad: '1', precio: '' }], notas: '',
  });
  const [pedidoGuardado, setPedidoGuardado] = useState(null);
  const [presupCart, setPresupCart] = useState([]);
  const [presupSearch, setPresupSearch] = useState('');
  const [presupCliente, setPresupCliente] = useState('');
  const [presupGDesc, setPresupGDesc] = useState(0);
  const [presupEnvioActivo, setPresupEnvioActivo] = useState(false);
  const [presupEnvioMonto, setPresupEnvioMonto] = useState(0);
  const [presupShowPreview, setPresupShowPreview] = useState(false);
  const [presupList, setPresupList] = useState([]);
  const [presupSavedId, setPresupSavedId] = useState(null);
  const [presupConfirmando, setPresupConfirmando] = useState(false);
  const [presupFormaPago, setPresupFormaPago] = useState('Efectivo');
  const [presupPagado, setPresupPagado] = useState('0');
  const [compareA, setCompareA] = useState('');
  const [compareB, setCompareB] = useState('');
  const [expandedPedido, setExpandedPedido] = useState(null);
  const [costosProv, setCostosProv] = useState({});
  const [editandoPedidoId, setEditandoPedidoId] = useState(null);
  const [fragBusqueda, setFragBusqueda]     = useState('');
  const [fragResultados, setFragResultados] = useState(null);
  const [fragCargando, setFragCargando]     = useState(false);
  const [fragPanel, setFragPanel]           = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [calcMargin, setCalcMargin] = useState(45);
  const [payMethod, setPayMethod] = useState('Efectivo');
  const [editandoFormaPago, setEditandoFormaPago] = useState(null);
  const [nuevaFormaPago, setNuevaFormaPago] = useState('');
  const [toast, setToast] = useState('');
  const [usuario, setUsuario] = useState(null);
  const nav = useNavigate();

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  useEffect(() => {
    const u = localStorage.getItem('ep_usuario');
    if (u) setUsuario(JSON.parse(u));
    axios.get(`${API}/auth/me`)
      .then(() => {
        cargarDatos();
        setDashLoading(true);
        fetch('/api/dashboard', getAuth()).then(r=>r.json()).then(d=>{setDashData(d);setDashLoading(false);}).catch(()=>setDashLoading(false));
        fetch('/api/visitas', getAuth()).then(r=>r.json()).then(v=>setVisitas(v)).catch(()=>{});
        fetch('/api/visitas/online', getAuth()).then(r=>r.json()).then(v=>setOnline(v.online)).catch(()=>{});
        fetch('/api/visitas/diarias', getAuth()).then(r=>r.json()).then(v=>setVisitasDiarias(v)).catch(()=>{});
      })
      .catch(() => nav('/login'));
    const onlinePoll = setInterval(() => {
      fetch('/api/visitas/online', getAuth()).then(r=>r.json()).then(v=>setOnline(v.online)).catch(()=>{});
    }, 30000);
    return () => clearInterval(onlinePoll);
  }, []);

  const cargarDatos = async () => {
    try {
      const [p, c, v] = await Promise.all([
        axios.get(`${API}/perfumes`),
        axios.get(`${API}/clientes`, getAuth()),
        axios.get(`${API}/ventas`, getAuth()),
      ]);
      setPerfumes(p.data);
      setClientes(c.data);
      setVentas(v.data);
    } catch (e) {
      if (e.response?.status === 401) nav('/login');
    }
    // Costos de proveedor (no bloqueante)
    fetch('/api/dashboard/costos', getAuth()).then(r=>r.ok?r.json():null).then(d=>{if(d)setCostosProv(d);}).catch(()=>{});
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    localStorage.removeItem('ep_usuario');
    nav('/login');
  };

  const actualizarStock = async (id, stock) => {
    await axios.patch(`${API}/perfumes/${id}/stock`, { stock }, getAuth());
    setPerfumes(prev => prev.map(p => p.id === id ? { ...p, stock } : p));
    showToast('Stock actualizado');
  };

  const actualizarPrecio = async (id) => {
    await axios.patch(`${API}/perfumes/${id}/precio`, { precio: parseFloat(nuevoPrecio) }, getAuth());
    setPerfumes(prev => prev.map(p => p.id === id ? { ...p, precio: parseFloat(nuevoPrecio) } : p));
    setEditandoPrecio(null);
    setNuevoPrecio('');
    showToast('Precio actualizado');
  };

  const actualizarDescuento = async (id) => {
    await axios.patch(`${API}/perfumes/${id}/descuento`, { descuento: parseInt(nuevoDescuento) }, getAuth());
    setPerfumes(prev => prev.map(p => p.id === id ? { ...p, descuento: parseInt(nuevoDescuento) } : p));
    setEditandoDescuento(null);
    showToast('Descuento actualizado');
  };

  const actualizarCosto = async (id) => {
    await axios.patch(`${API}/perfumes/${id}/costo`, { costo: parseFloat(nuevoCosto) }, getAuth());
    setPerfumes(prev => prev.map(p => p.id === id ? { ...p, costo: parseFloat(nuevoCosto) } : p));
    setEditandoCosto(null);
    setNuevoCosto('');
    showToast('Costo actualizado');
  };

  const eliminarVenta = async (id) => {
    try {
      await axios.delete(`${API}/ventas/${id}`, getAuth());
      setVentas(prev => prev.filter(v => v.id !== id));
      cargarDatos();
      showToast('Venta eliminada y stock restaurado');
    } catch { showToast('Error al eliminar la venta'); }
  };

  const eliminarPedido = async (id) => {
    try {
      await axios.delete(`${API}/proveedores/pedidos/${id}`, getAuth());
      setPedidosProv(prev => prev.filter(p => p.id !== id));
      showToast('Presupuesto eliminado');
    } catch { showToast('Error al eliminar el presupuesto'); }
  };

  const buscarEnFragrantica = async (marca, nombre, perfumeId) => {
    if (!nombre) return;
    setFragCargando(true); setFragResultados(null);
    try {
      const r = await axios.get(
        `${API}/perfumes/fragrantica?marca=${encodeURIComponent(marca)}&nombre=${encodeURIComponent(nombre)}`,
        getAuth()
      );
      if (r.data.ok && r.data.notas?.length > 0) {
        setFragResultados({ notas: r.data.notas, url: r.data.url });
        setFragBusqueda(r.data.notas.join(', '));
      } else {
        setFragResultados({ notas: [], url: r.data.url });
        setFragBusqueda('');
      }
    } catch { showToast('Error al buscar en Parfumo'); }
    setFragCargando(false);
  };

  const cargarDetalleFragrantica = () => {};  // no usado

  const guardarNotasFragrantica = async (perfumeId, notasTexto) => {
    if (!notasTexto.trim()) return;
    const notas = notasTexto.split(/[,;\n]+/).map(n => n.trim()).filter(n => n.length > 1).join(', ');
    try {
      await axios.patch(`${API}/perfumes/${perfumeId}/notas`, { notas }, getAuth());
      setPerfumes(prev => prev.map(p => p.id === perfumeId ? { ...p, notas } : p));
      setEditPerfumeForm(f => ({ ...f, notas }));
      showToast('✅ Notas guardadas');
      setFragPanel(false); setFragBusqueda(''); setFragResultados(null);
    } catch { showToast('Error al guardar notas'); }
  };

  const actualizarFotos = async (id) => {
    await axios.put(`${API}/perfumes/${id}`, { ...perfumes.find(p => p.id === id), ...editandoFotoUrls }, getAuth());
    setPerfumes(prev => prev.map(p => p.id === id ? { ...p, ...editandoFotoUrls } : p));
    setEditandoFoto(null);
    setEditandoFotoUrls({});
    showToast('Fotos actualizadas');
  };

  const actualizarFoto = async (id) => {
    await axios.put(`${API}/perfumes/${id}`, { ...perfumes.find(p => p.id === id), imagen_url: nuevaFoto }, getAuth());
    setPerfumes(prev => prev.map(p => p.id === id ? { ...p, imagen_url: nuevaFoto } : p));
    setEditandoFoto(null);
    setNuevaFoto('');
    showToast('Foto actualizada');
  };

  const guardarEdicionPerfume = async (id) => {
    try {
      const r = await axios.put(`${API}/perfumes/${id}`, {
        ...editPerfumeForm,
        precio: parseFloat(editPerfumeForm.precio),
        costo: parseFloat(editPerfumeForm.costo || 0),
        stock: parseInt(editPerfumeForm.stock || 0),
        ml: parseInt(editPerfumeForm.ml || 100),
      }, getAuth());
      // Usar la respuesta del servidor (con inspiracion ya guardada) para actualizar el estado local
      const guardado = r.data;
      if (guardado && guardado.id) {
        setPerfumes(prev => prev.map(p => p.id === id ? { ...p, ...guardado } : p));
        setEditandoPerfume(null);
        showToast('Perfume actualizado');
      } else {
        showToast('Error: respuesta inesperada del servidor');
      }
    } catch (err) {
      showToast('Error al guardar: ' + (err.response?.data?.error || err.message));
    }
  };

  const subirFoto = async (file, onUrl) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('foto', file);
    try {
      const r = await axios.post(`${API}/upload`, fd, {
        headers: { ...getAuth().headers, 'Content-Type': 'multipart/form-data' }
      });
      onUrl(r.data.url);
      showToast('Foto subida OK');
    } catch { showToast('Error al subir la foto'); }
  };

  const agregarPerfume = async () => {
    if (!nuevoPerfumeForm.nombre || !nuevoPerfumeForm.precio) { showToast('Completa nombre y precio'); return; }
    const r = await axios.post(`${API}/perfumes`, {
      ...nuevoPerfumeForm,
      precio: parseFloat(nuevoPerfumeForm.precio),
      costo: parseFloat(nuevoPerfumeForm.costo || 0),
      stock: parseInt(nuevoPerfumeForm.stock || 0),
      ml: parseInt(nuevoPerfumeForm.ml || 100),
    }, getAuth());
    setPerfumes(prev => [...prev, r.data]);
    setNuevoPerfumeForm(EMPTY_PERFUME);
    showToast('Perfume agregado');
  };

  const guardarFormaPago = async (ventaId) => {
    if (!nuevaFormaPago) return;
    await axios.patch(`${API}/ventas/${ventaId}/forma_pago`, { forma_pago: nuevaFormaPago }, getAuth());
    setVentas(prev => prev.map(v => v.id === ventaId ? { ...v, forma_pago: nuevaFormaPago } : v));
    setEditandoFormaPago(null);
    setNuevaFormaPago('');
    showToast('Forma de pago actualizada');
  };

  const agregarClienteDesdeVenta = async () => {
    if (!nuevoClienteForm.nombre) { showToast('Ingresa el nombre del cliente'); return; }
    const r = await axios.post(`${API}/clientes`, nuevoClienteForm, getAuth());
    setClientes(prev => [...prev, r.data]);
    setVentaForm(f => ({ ...f, clienteId: String(r.data.id) }));
    setNuevoClienteForm({ nombre: '', telefono: '', email: '', dni: '' });
    setMostrarNuevoCliente(false);
    showToast(`Cliente ${r.data.nombre} agregado`);
  };

  const registrarPago = async () => {
    const monto = parseFloat(payAmount);
    if (!monto || monto <= 0) return;
    const venta = ventas.find(v => v.id === payingVenta);
    await axios.patch(`${API}/ventas/${payingVenta}/pago`, { monto, forma_pago: payMethod }, getAuth());
    showToast(`Pago de ${fmt(monto)} registrado`);
    if (venta) imprimirRecibo(venta, { tipoPago: payMethod, montoPago: monto });
    setPayingVenta(null);
    setPayAmount('');
    setPayMethod('Efectivo');
    cargarDatos();
  };

  const agregarExtra = () => {
    setExtrasVenta(prev => [...prev, { id: Date.now(), desc: '', precio: '' }]);
  };
  const actualizarExtra = (id, campo, valor) => {
    setExtrasVenta(prev => prev.map(e => e.id === id ? { ...e, [campo]: valor } : e));
  };
  const eliminarExtra = (id) => {
    setExtrasVenta(prev => prev.filter(e => e.id !== id));
  };

  const toggleProd = p => {
    setSelProds(prev => {
      const found = prev.find(x => x.id === p.id);
      if (found) return prev.filter(x => x.id !== p.id);
      const precioFinal = p.descuento > 0 ? Math.round(p.precio * (1 - p.descuento / 100)) : p.precio;
      return [...prev, { ...p, qty: 1, precio: precioFinal }];
    });
  };

  const updQty = (id, qty) => setSelProds(prev => prev.map(x => x.id === id ? { ...x, qty: Math.max(1, qty) } : x));
  const totalVenta = selProds.reduce((a, p) => a + p.precio * p.qty, 0) + extrasVenta.reduce((a, e) => a + (parseFloat(e.precio)||0), 0);

  const imprimirRecibo = (venta, opciones = {}) => {
    const { tipoPago = null, montoPago = null } = opciones; // si tipoPago, es un recibo de pago de saldo
    const fechaStr = new Date().toLocaleDateString('es-AR');
    const items = (venta.items || []).filter(i => i.nombre);
    const total = parseFloat(venta.total || 0);
    const pagado = parseFloat(venta.pagado ?? venta.pago ?? 0);
    const saldo = Math.max(0, total - pagado);
    const clienteNombre = venta.cliente_nombre || clientes.find(c => c.id === parseInt(venta.cliente_id))?.nombre || '—';
    const clienteDni = clientes.find(c => c.nombre === clienteNombre)?.dni || '';
    const numRecibo = venta.id ? String(venta.id).padStart(6, '0') : '------';

    const filas = items.map(i => `
      <tr>
        <td style="padding:8px 0; font-size:13px; color:#111;">${i.nombre}</td>
        <td style="padding:8px 0; font-size:13px; color:#888; text-align:center;">${i.cantidad}</td>
        <td style="padding:8px 0; font-size:13px; color:#888; text-align:right;">${fmt(i.precio)}</td>
        <td style="padding:8px 0; font-size:13px; color:#111; text-align:right; font-weight:600;">${fmt(i.precio * i.cantidad)}</td>
      </tr>
    `).join('');

    const tituloDoc = tipoPago ? 'Recibo de pago' : 'Recibo de venta';
    const saldoNuevo = tipoPago ? Math.max(0, saldo - parseFloat(montoPago||0)) : saldo;
    const pagadoNuevo = tipoPago ? pagado + parseFloat(montoPago||0) : pagado;

    const bloquePago = tipoPago ? `
      <div class="totales">
        <div class="tot-row"><span>Total de la venta</span><span>${fmt(total)}</span></div>
        <div class="tot-row"><span>Pagado anteriormente</span><span>${fmt(pagado)}</span></div>
        <div class="tot-final"><span>Pago recibido hoy</span><span class="serif">${fmt(montoPago)}</span></div>
        ${saldoNuevo > 0
          ? `<div class="tot-row saldo" style="margin-top:8px;"><span>Nuevo saldo pendiente</span><span>${fmt(saldoNuevo)}</span></div>`
          : `<div class="tot-row" style="margin-top:8px; color:#27ae60; font-weight:600;"><span>Estado</span><span>Saldado ✓</span></div>`}
      </div>
    ` : `
      <div class="totales">
        <div class="tot-row"><span>Pagado</span><span>${fmt(pagado)}</span></div>
        ${saldo > 0 ? `<div class="tot-row saldo"><span>Saldo pendiente</span><span>${fmt(saldo)}</span></div>` : ''}
        <div class="tot-final"><span>Total</span><span class="serif">${fmt(total)}</span></div>
      </div>
    `;

    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${tituloDoc} #${numRecibo}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600&family=DM+Sans:wght@400;500;600&display=swap');
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'DM Sans',sans-serif; color:#111; padding:40px; max-width:480px; margin:0 auto; }
    .serif { font-family:'Cormorant Garamond',Georgia,serif; }
    .header { text-align:center; margin-bottom:24px; padding-bottom:20px; border-bottom:1px solid #ececec; }
    .logo { font-size:22px; font-weight:600; letter-spacing:4px; text-transform:uppercase; }
    .logo span { color:#c9a96e; font-style:italic; }
    .sub { font-size:10px; color:#bbb; letter-spacing:2px; text-transform:uppercase; margin-top:6px; }
    .info-row { display:flex; justify-content:space-between; font-size:12px; color:#888; margin-bottom:4px; }
    .info-row strong { color:#111; }
    table { width:100%; border-collapse:collapse; margin:20px 0; }
    thead th { font-size:9px; color:#bbb; text-transform:uppercase; letter-spacing:1.5px; text-align:left; padding-bottom:8px; border-bottom:1px solid #ececec; }
    thead th:nth-child(2) { text-align:center; }
    thead th:nth-child(3), thead th:nth-child(4) { text-align:right; }
    tbody tr { border-bottom:1px solid #f5f5f5; }
    .totales { margin-top:16px; padding-top:12px; border-top:1px solid #ececec; }
    .tot-row { display:flex; justify-content:space-between; font-size:13px; padding:3px 0; color:#888; }
    .tot-final { display:flex; justify-content:space-between; font-size:20px; padding-top:8px; margin-top:6px; border-top:1px solid #ececec; font-weight:600; }
    .tot-final .serif { font-size:24px; color:#c9a96e; }
    .saldo { color:#E24B4A; font-weight:600; }
    .footer { text-align:center; margin-top:32px; padding-top:16px; border-top:1px solid #ececec; font-size:10px; color:#bbb; letter-spacing:1px; }
    @media print { body { padding:20px; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo serif">Essence <span>Pure</span></div>
    <div class="sub">Perfumería Árabe · ${tituloDoc}</div>
  </div>

  <div class="info-row"><span>Recibo N°</span><strong>${numRecibo}${tipoPago ? '-P' : ''}</strong></div>
  <div class="info-row"><span>Fecha</span><strong>${fechaStr}</strong></div>
  <div class="info-row"><span>Cliente</span><strong>${clienteNombre}</strong></div>
  ${clienteDni ? `<div class="info-row"><span>DNI/CUIT</span><strong>${clienteDni}</strong></div>` : ''}
  <div class="info-row"><span>Forma de pago</span><strong>${(tipoPago || venta.forma_pago) || '—'}</strong></div>

  ${!tipoPago ? `
  <table>
    <thead>
      <tr><th>Producto</th><th>Cant.</th><th>Precio</th><th>Subtotal</th></tr>
    </thead>
    <tbody>
      ${filas}
    </tbody>
  </table>
  ` : `
  <div style="margin:16px 0; font-size:11px; color:#bbb; text-transform:uppercase; letter-spacing:1px;">Productos de la venta original</div>
  <div style="font-size:12px; color:#888; margin-bottom:8px;">${items.map(i => `${i.nombre}${i.cantidad>1?' ×'+i.cantidad:''}`).join(', ')}</div>
  `}

  ${bloquePago}

  <div class="footer">✦ Gracias por tu compra ✦<br/>Essence Pure · essencepure.carpell.com.ar</div>

  <script>window.onload = () => window.print();</script>
</body>
</html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  };

  const confirmarVenta = async () => {
    const extrasValidos = extrasVenta.filter(e => e.desc && e.precio);
    if (!ventaForm.clienteId || (selProds.length === 0 && extrasValidos.length === 0)) { showToast('Elegi cliente y al menos un perfume o extra'); return; }
    const itemsPerfumes = selProds.map(p => ({ perfume_id: p.id, nombre: p.nombre, cantidad: p.qty, precio: p.precio }));
    const itemsExtras = extrasValidos.map(e => ({ perfume_id: null, nombre: e.desc, cantidad: 1, precio: parseFloat(e.precio) }));
    const items = [...itemsPerfumes, ...itemsExtras];
    const ventaData = {
      cliente_id: parseInt(ventaForm.clienteId),
      fecha: ventaForm.date,
      total: totalVenta,
      pagado: parseFloat(ventaForm.paid || 0),
      forma_pago: ventaForm.method,
      items
    };
    const r = await axios.post(`${API}/ventas`, ventaData, getAuth());
    setSelProds([]);
    setExtrasVenta([]);
    setVentaForm({ clienteId: '', paid: '', method: 'Efectivo', date: new Date().toISOString().split('T')[0] });
    showToast('Venta registrada');
    cargarDatos();
    // Imprimir recibo con los datos de la venta recién creada
    const ventaCreada = { ...ventaData, id: r.data?.id, items, cliente_nombre: clientes.find(c => c.id === ventaData.cliente_id)?.nombre };
    imprimirRecibo(ventaCreada);
  };

  const exportarCatalogoPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18); doc.setTextColor(184,134,11);
    doc.text('essence pure - Catalogo de Perfumes', 14, 20);
    doc.setFontSize(10); doc.setTextColor(100);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-AR')}`, 14, 27);
    autoTable(doc, {
      startY: 32,
      head: [['Nombre','Marca','Familia','ML','Precio','Desc.','Precio c/desc.','Stock']],
      body: perfumes.map(p => [p.nombre, p.marca, p.familia, p.ml, fmt(p.precio), p.descuento?`${p.descuento}%`:'-', p.descuento?fmt(Math.round(p.precio*(1-p.descuento/100))):'-', p.stock]),
      headStyles: { fillColor: [184,134,11] },
      alternateRowStyles: { fillColor: [255,253,240] },
    });
    doc.save('essencepure-catalogo.pdf');
  };

  const exportarClientesPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18); doc.setTextColor(184,134,11);
    doc.text('essence pure - Clientes y Ventas', 14, 20);
    doc.setFontSize(10); doc.setTextColor(100);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-AR')}`, 14, 27);
    let y = 32;
    clientes.forEach(c => {
      const ventasC = ventas.filter(v => v.cliente_id === c.id);
      if (ventasC.length === 0) return;
      const deuda = ventasC.reduce((a,v)=>a+parseFloat(v.saldo||0),0);
      const total = ventasC.reduce((a,v)=>a+parseFloat(v.total||0),0);
      autoTable(doc, {
        startY: y,
        head: [[`${c.nombre} - Total: ${fmt(total)} - Deuda: ${fmt(deuda)}`]],
        body: ventasC.map(v => [`${v.fecha?.split('T')[0]} | ${v.items?.filter(i=>i.nombre).map(i=>i.nombre).join(', ')} | Total: ${fmt(v.total)} | Pago: ${fmt(v.pagado)} | Debe: ${fmt(v.saldo)} | ${v.forma_pago}`]),
        headStyles: { fillColor: [95,168,160] },
        bodyStyles: { fontSize: 9 },
      });
      y = doc.lastAutoTable.finalY + 6;
      if (y > 270) { doc.addPage(); y = 14; }
    });
    doc.save('essencepure-clientes.pdf');
  };

  const exportarClientesExcel = () => {
    const rows = [];
    clientes.forEach(c => {
      ventas.filter(v => v.cliente_id === c.id).forEach(v => {
        rows.push({ Cliente: c.nombre, Telefono: c.telefono||'', Fecha: v.fecha?.split('T')[0], Productos: v.items?.filter(i=>i.nombre).map(i=>i.nombre).join(', '), Total: parseFloat(v.total), Pagado: parseFloat(v.pagado), Saldo: parseFloat(v.saldo), FormaPago: v.forma_pago });
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ventas');
    XLSX.writeFile(wb, 'essencepure-ventas.xlsx');
  };

  const exportarCatalogoExcel = () => {
    const rows = perfumes.map(p => ({ Nombre: p.nombre, Marca: p.marca, Familia: p.familia, ML: p.ml, Precio: p.precio, Descuento: p.descuento||0, PrecioConDesc: p.descuento?Math.round(p.precio*(1-p.descuento/100)):p.precio, Costo: p.costo, Stock: p.stock, Notas: p.notas||'', Origen: p.origen||'' }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Catalogo');
    XLSX.writeFile(wb, 'essencepure-catalogo.xlsx');
  };

  const exportarBackup = () => {
    const data = { perfumes, clientes, ventas, fecha: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `essencepure-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click(); URL.revokeObjectURL(url);
    showToast('Backup descargado');
  };

  const miniImg = p => (
    <div style={{ width: 45, height: 45, borderRadius: 8, overflow: 'hidden', background: '#f5f0e8', flexShrink: 0 }}>
      {p.imagen_url
        ? <img src={p.imagen_url} alt={p.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display='none'} />
        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{p.familia==='Femenino'?'x':'o'}</div>
      }
    </div>
  );

  const MAIN_TABS = [
    { id: 'dashboard',   label: 'Dashboard',   icon: 'ti-dashboard' },
    { id: 'perfumes',    label: 'Perfumes',     icon: 'ti-bottle' },
    { id: 'ventas',      label: 'Ventas',       icon: 'ti-receipt' },
    { id: 'clientes',    label: 'Clientes',     icon: 'ti-users' },
    { id: 'proveedores', label: 'Proveedores',  icon: 'ti-truck' },
    { id: 'presupuesto', label: 'Presupuesto', icon: 'ti-file-invoice' },
    { id: 'banner',      label: 'Banner',       icon: 'ti-speakerphone' },
  ];

  const PERFUME_SUBTABS = [
    { id: 'listado',   label: 'Listado' },
    { id: 'stock',     label: 'Stock' },
    { id: 'precios',   label: 'Precios' },
    { id: 'editar',    label: 'Editar' },
    { id: 'agregar',   label: 'Agregar' },
    { id: 'fotos',     label: 'Fotos' },
    { id: 'exportar',  label: 'Exportar' },
  ];

  const filtradosVenta = perfumes.filter(p => {
    const ms = p.nombre.toLowerCase().includes(ventaSearch.toLowerCase());
    const mc = ventaFiltro === 'Todos' || p.familia === ventaFiltro;
    return ms && mc;
  });

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: '#fafaf8', minHeight: '100vh', paddingBottom: 40 }}>

      {toast && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#111', color: '#fff', padding: '10px 22px', borderRadius: 2, zIndex: 9999, fontSize: 12, letterSpacing: 0.5 }}>{toast}</div>}

      {/* HEADER */}
      <div style={{ background: '#fff', padding: '13px 24px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #ececec' }}>
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 17, fontWeight: 600, color: '#111', letterSpacing: 3, textTransform: 'uppercase' }}>
            Essence <span style={{ color: '#c9a96e', fontStyle: 'italic' }}>Pure</span>
          </div>
          <div style={{ fontSize: 10, color: '#bbb', letterSpacing: 1 }}>Admin · {usuario?.nombre}</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <a href="/" style={{ padding: '8px 14px', border: '1px solid #e0e0e0', borderRadius: 2, textDecoration: 'none', fontSize: 11, color: '#555', fontWeight: 500, letterSpacing: 0.5 }}>Ver catálogo</a>
          <button style={{ padding: '8px 14px', background: '#111', color: '#fff', border: 'none', borderRadius: 2, fontSize: 11, fontWeight: 500, cursor: 'pointer', letterSpacing: 0.5 }} onClick={logout}>Salir</button>
        </div>
      </div>

      {/* MAIN TABS */}
      <div style={{ background: '#fff', borderBottom: '1px solid #ececec', padding: '0 24px', display: 'flex', gap: 0, overflowX: 'auto' }}>
        {MAIN_TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '12px 18px', border: 'none', background: 'none',
            borderBottom: tab === t.id ? '2px solid #c9a96e' : '2px solid transparent',
            color: tab === t.id ? '#111' : '#aaa',
            fontWeight: tab === t.id ? 600 : 400,
            cursor: 'pointer', fontSize: 12,
            fontFamily: "'DM Sans',sans-serif",
            letterSpacing: 0.5, whiteSpace: 'nowrap',
            display: 'flex', alignItems: 'center', gap: 6,
            transition: 'all 0.2s',
          }}>
            <i className={`ti ${t.icon}`} aria-hidden="true" style={{ fontSize: 14 }}></i>
            {t.label}
          </button>
        ))}
      </div>

      {/* PERFUMES SUB-TABS */}
      {tab === 'perfumes' && (
        <div style={{ background: '#f5f5f3', borderBottom: '1px solid #e8e8e8', padding: '8px 24px', display: 'flex', gap: 6, overflowX: 'auto' }}>
          {PERFUME_SUBTABS.map(t => (
            <button key={t.id} onClick={() => setSubTab(t.id)} style={{
              padding: '6px 14px', border: 'none', borderRadius: 2,
              background: subTab === t.id ? '#111' : 'transparent',
              color: subTab === t.id ? '#fff' : '#888',
              fontWeight: subTab === t.id ? 600 : 400,
              cursor: 'pointer', fontSize: 11,
              fontFamily: "'DM Sans',sans-serif",
              letterSpacing: 1, textTransform: 'uppercase',
              transition: 'all 0.2s', whiteSpace: 'nowrap',
            }}>
              {t.label}
            </button>
          ))}
        </div>
      )}
      {/* STOCK */}
      {/* ── DASHBOARD ─────────────────────────────────────── */}
      {tab === 'dashboard' && (() => {
        const d = dashData || {};
        const fmtN = n => Math.round(Number(n) || 0).toLocaleString('es-AR');
        const initials = name => (name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
        const avatarColors = ['#E6F1FB','#EAF3DE','#FAEEDA','#FBEAF0','#E1F5EE'];
        const avatarText   = ['#0C447C','#3B6D11','#854F0B','#993556','#0F6E56'];

        return (
          <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>

            {/* MÉTRICAS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 20 }}>
              {[
                { label: 'Fragancias', val: perfumes.length || 0, sub: `${perfumes.filter(p=>p.stock>0).length} con stock` },
                { label: 'Ventas este mes', val: `$${fmtN(d.ventasMes?.total_mes)}`, sub: `${d.ventasMes?.count||0} órdenes` },
                { label: 'Total histórico', val: `$${fmtN(ventas.reduce((s,v)=>s+Number(v.total||0),0))}`, sub: `${ventas.length} ventas` },
                { label: 'Deuda pendiente', val: `$${fmtN(d.ventasMes?.deuda_mes)}`, sub: `${(d.deudaClientes||[]).length} clientes`, danger: Number(d.ventasMes?.deuda_mes) > 0 },
              ].map((m,i) => (
                <div key={i} style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 2, padding: '14px 16px' }}>
                  <div style={{ fontSize: 10, color: '#bbb', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>{m.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 600, color: m.danger ? '#E24B4A' : '#111', fontFamily: "'Cormorant Garamond',serif", lineHeight: 1 }}>{m.val}</div>
                  <div style={{ fontSize: 10, color: '#bbb', marginTop: 5 }}>{m.sub}</div>
                </div>
              ))}
            </div>

            {/* VISITANTES */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
              {[
                { label: 'Visitantes hoy',       val: visitas ? Number(visitas.hoy)    : '—', color: '#2563eb' },
                { label: 'Esta semana',           val: visitas ? Number(visitas.semana) : '—', color: '#7c3aed' },
                { label: 'Este mes',              val: visitas ? Number(visitas.mes)    : '—', color: '#059669' },
                { label: 'Online ahora',          val: online  !== null ? online        : '—', color: online > 0 ? '#16a34a' : '#888', live: true },
              ].map((m,i) => (
                <div key={i} style={{ background: '#fff', border: `1px solid ${m.live && online > 0 ? '#bbf7d0' : '#ececec'}`, borderRadius: 2, padding: '14px 16px', position: 'relative' }}>
                  {m.live && online > 0 && (
                    <span style={{ position: 'absolute', top: 10, right: 10, width: 8, height: 8, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                  )}
                  <div style={{ fontSize: 10, color: '#bbb', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>{m.label}</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: m.color, fontFamily: "'Cormorant Garamond',serif", lineHeight: 1 }}>{m.val}</div>
                  {m.live && <div style={{ fontSize: 9, color: '#bbb', marginTop: 4 }}>actualiza cada 30s</div>}
                </div>
              ))}
            </div>

            {/* GRÁFICOS */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>

              {/* VENTAS Y VISITAS — últimos 30 días */}
              <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 2, padding: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 14 }}>Actividad — últimos 30 días</div>
                {(() => {
                  const ventasMap = {};
                  (d.ventasDiarias||[]).forEach(x => { ventasMap[x.dia] = { ventas: x.ventas, revenue: Number(x.revenue) }; });
                  const visitasMap = {};
                  visitasDiarias.forEach(x => { visitasMap[x.dia] = x.visitas; });
                  const allDays = [...new Set([...Object.keys(ventasMap), ...Object.keys(visitasMap)])].sort();
                  const data = allDays.map(dia => ({
                    dia: dia.slice(5),
                    Ventas: ventasMap[dia]?.ventas || 0,
                    Visitas: visitasMap[dia] || 0,
                  }));
                  if (data.length === 0) return <div style={{ fontSize: 12, color: '#bbb', textAlign: 'center', padding: 30 }}>Sin datos aún</div>;
                  return (
                    <ResponsiveContainer width="100%" height={180}>
                      <AreaChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="dia" tick={{ fontSize: 9, fill: '#bbb' }} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 9, fill: '#bbb' }} />
                        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4 }} />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                        <Area type="monotone" dataKey="Visitas" stroke="#6366f1" fill="#ede9fe" strokeWidth={1.5} dot={false} />
                        <Area type="monotone" dataKey="Ventas"  stroke="#c9a96e" fill="#fef3e2" strokeWidth={2} dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  );
                })()}
              </div>

              {/* TOP PERFUMES — gráfico de barras */}
              <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 2, padding: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 14 }}>Top perfumes este mes</div>
                {(d.topPerfumes||[]).length === 0
                  ? <div style={{ fontSize: 12, color: '#bbb', textAlign: 'center', padding: 30 }}>Sin ventas este mes</div>
                  : (() => {
                      const data = (d.topPerfumes||[]).map(p => ({
                        nombre: p.nombre_perfume?.split(' ').slice(0,2).join(' ') || '—',
                        Unidades: p.total_vendido,
                      }));
                      return (
                        <ResponsiveContainer width="100%" height={180}>
                          <BarChart data={data} layout="vertical" margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 9, fill: '#bbb' }} />
                            <YAxis type="category" dataKey="nombre" tick={{ fontSize: 9, fill: '#444' }} width={70} />
                            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4 }} />
                            <Bar dataKey="Unidades" fill="#c9a96e" radius={[0, 3, 3, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      );
                    })()
                }
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>

              {/* MÁS VENDIDOS MES */}
              <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 2, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: 2 }}>Más vendidos este mes</div>
                  <button onClick={() => { setDashLoading(true); fetch('/api/dashboard',getAuth()).then(r=>r.json()).then(d=>{setDashData(d);setDashLoading(false);}).catch(()=>setDashLoading(false)); fetch('/api/visitas',getAuth()).then(r=>r.json()).then(v=>setVisitas(v)).catch(()=>{}); }}
                    style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 2, padding: '4px 10px', fontSize: 10, cursor: 'pointer', color: '#888' }}>
                    {dashLoading ? '...' : '↺'}
                  </button>
                </div>
                {dashLoading && !dashData && <div style={{ textAlign:'center', color:'#bbb', padding: 20, fontSize: 12 }}>Cargando...</div>}
                {(d.topPerfumes||[]).length === 0 && !dashLoading && <div style={{ fontSize: 12, color: '#bbb', textAlign: 'center', padding: 20 }}>Sin ventas este mes</div>}
                {(d.topPerfumes||[]).map((p,i) => (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: '#111', fontWeight: 500 }}>{p.nombre_perfume}</span>
                      <span style={{ color: '#888' }}>{p.total_vendido} uds</span>
                    </div>
                    <div style={{ height: 5, background: '#f0f0f0', borderRadius: 2 }}>
                      <div style={{ height: '100%', width: `${Math.round((p.total_vendido / ((d.topPerfumes||[{}])[0]?.total_vendido||1))*100)}%`, background: '#c9a96e', borderRadius: 2 }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* MÁS VENDIDOS HISTÓRICO */}
              <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 2, padding: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 14 }}>Más vendidos histórico</div>
                {(() => {
                  const hist = {};
                  ventas.forEach(v => {
                    (v.items||[]).forEach(item => {
                      if (!item.nombre) return;
                      hist[item.nombre] = (hist[item.nombre]||0) + Number(item.cantidad||1);
                    });
                  });
                  const top = Object.entries(hist).sort((a,b)=>b[1]-a[1]).slice(0,5);
                  const max = top[0]?.[1] || 1;
                  return top.length === 0
                    ? <div style={{ fontSize: 12, color: '#bbb', textAlign: 'center', padding: 20 }}>Sin datos</div>
                    : top.map(([nombre,qty],i) => (
                      <div key={i} style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                          <span style={{ color: '#111', fontWeight: 500 }}>{nombre}</span>
                          <span style={{ color: '#888' }}>{qty} uds</span>
                        </div>
                        <div style={{ height: 5, background: '#f0f0f0', borderRadius: 2 }}>
                          <div style={{ height: '100%', width: `${Math.round((qty/max)*100)}%`, background: '#111', borderRadius: 2 }} />
                        </div>
                      </div>
                    ));
                })()}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

              {/* STOCK CRÍTICO */}
              <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 2, padding: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 14 }}>Stock crítico</div>
                {(d.stockBajo||perfumes.filter(p=>p.stock<=3)).length === 0
                  ? <div style={{ fontSize: 12, color: '#27ae60', textAlign: 'center', padding: 16 }}>Todo en orden</div>
                  : (d.stockBajo||perfumes.filter(p=>p.stock<=3)).map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#fafaf8', borderRadius: 2, marginBottom: 6, border: '1px solid #ececec' }}>
                      <span style={{ fontSize: 12, color: '#111' }}>{p.nombre}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 2,
                        background: p.stock===0 ? '#FCEBEB' : '#FAEEDA',
                        color: p.stock===0 ? '#A32D2D' : '#854F0B' }}>
                        {p.stock===0 ? 'Sin stock' : `${p.stock} ud`}
                      </span>
                    </div>
                  ))
                }
              </div>

              {/* ÚLTIMAS VENTAS */}
              <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 2, padding: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 14 }}>Últimas ventas</div>
                {(d.ultimasVentas||ventas.slice(-5).reverse()).slice(0,5).map((v,i) => (
                  <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: avatarColors[i%5], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: avatarText[i%5], flexShrink: 0 }}>
                      {initials(v.cliente_nombre||v.cliente?.nombre||'?')}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: '#111' }}>{v.cliente_nombre||v.cliente?.nombre||'—'}</div>
                      <div style={{ fontSize: 10, color: '#bbb' }}>{v.forma_pago} · {v.fecha ? new Date(v.fecha).toLocaleDateString('es-AR') : ''}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>${fmtN(v.total)}</div>
                      {Number(v.pagado) < Number(v.total) && (
                        <div style={{ fontSize: 10, color: '#E24B4A', fontWeight: 600 }}>Debe ${fmtN(Number(v.total)-Number(v.pagado))}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── PERFUMES ────────────────────────────────────────── */}
      {tab === 'perfumes' && subTab === 'listado' && (() => {
        const fmtP = n => n > 0 ? `$${Math.round(n).toLocaleString('es-AR')}` : '—';
        const busqL = listadoBusq, setBusqL = setListadoBusq;
        const filtL = listadoFilt, setFiltL = setListadoFilt;
        const lista = perfumes
          .filter(p => {
            const q = busqL.toLowerCase();
            const match = !q || p.nombre.toLowerCase().includes(q) || (p.marca||'').toLowerCase().includes(q) || (p.notas||'').toLowerCase().includes(q);
            const hd = parseFloat(p.precio_decant_5ml) > 0 || parseFloat(p.precio_decant_10ml) > 0;
            const filtMatch =
              filtL === 'todos'   ? true :
              filtL === 'stock'   ? p.stock > 0 :
              filtL === 'decant'  ? hd :
              filtL === 'sinstk'  ? (!p.stock || p.stock <= 0) && !hd : true;
            return match && filtMatch;
          })
          .sort((a, b) => a.nombre.localeCompare(b.nombre));
        return (
          <div style={{ padding: '14px 16px' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                style={{ ...S.inp, flex: 1, minWidth: 200 }}
                placeholder="Buscar por nombre, marca o notas..."
                value={busqL}
                onChange={e => setBusqL(e.target.value)}
              />
              {[
                { v: 'todos',  l: 'Todos' },
                { v: 'stock',  l: '🍾 Con frasco' },
                { v: 'decant', l: '🧪 Con decant' },
                { v: 'sinstk', l: 'Sin stock' },
              ].map(f => (
                <button key={f.v} onClick={() => setFiltL(f.v)}
                  style={{ padding: '7px 14px', fontSize: 11, fontWeight: 600, borderRadius: 20, cursor: 'pointer', border: filtL===f.v ? '2px solid #b8860b' : '1px solid #e0e0e0', background: filtL===f.v ? '#fff9ee' : '#fafafa', color: filtL===f.v ? '#b8860b' : '#888' }}>
                  {f.l}
                </button>
              ))}
              <span style={{ fontSize: 11, color: '#bbb', marginLeft: 4 }}>{lista.length} perfume{lista.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Cabecera */}
            <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr 1fr 100px 80px 100px', gap: 10, padding: '6px 10px', fontSize: 9, color: '#bbb', textTransform: 'uppercase', letterSpacing: 1.5, borderBottom: '2px solid #f0ede8' }}>
              <div />
              <div>Perfume</div>
              <div>Notas</div>
              <div>Precio</div>
              <div>Stock</div>
              <div>Decants</div>
            </div>

            {lista.map((p, i) => {
              const precio = parseFloat(p.precio) || 0;
              const pf = p.descuento > 0 ? Math.round(precio * (1 - p.descuento / 100)) : precio;
              const d5  = parseFloat(p.precio_decant_5ml)  || 0;
              const d10 = parseFloat(p.precio_decant_10ml) || 0;
              const hd  = d5 > 0 || d10 > 0;
              const stockOk = p.stock > 0;
              const tipo = stockOk && hd ? 'ambos' : stockOk ? 'frasco' : hd ? 'decant' : 'ninguno';
              return (
                <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '48px 1fr 1fr 100px 80px 100px', gap: 10, alignItems: 'center', padding: '10px 10px', background: i % 2 === 0 ? '#fff' : '#fafaf8', borderBottom: '1px solid #f5f0e8' }}>

                  {/* Imagen */}
                  {miniImg(p)}

                  {/* Nombre + Marca */}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#111', lineHeight: 1.3 }}>{p.nombre}</div>
                    <div style={{ fontSize: 10, color: '#b8860b', textTransform: 'uppercase', letterSpacing: 1, marginTop: 2 }}>{p.marca}</div>
                    <div style={{ fontSize: 10, color: '#bbb', marginTop: 1 }}>{p.familia}{p.ml ? ` · ${p.ml}ml` : ''}</div>
                  </div>

                  {/* Notas */}
                  <div style={{ fontSize: 11, color: '#666', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                    {p.notas || <span style={{ color: '#ccc', fontStyle: 'italic' }}>Sin notas</span>}
                  </div>

                  {/* Precio */}
                  <div>
                    {precio > 0 ? (
                      <>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#111' }}>{fmtP(pf)}</div>
                        {p.descuento > 0 && <div style={{ fontSize: 10, color: '#bbb', textDecoration: 'line-through' }}>{fmtP(precio)}</div>}
                        {p.descuento > 0 && <div style={{ fontSize: 9, color: '#c0392b', fontWeight: 700 }}>-{p.descuento}%</div>}
                      </>
                    ) : <span style={{ color: '#ccc', fontSize: 12 }}>—</span>}
                  </div>

                  {/* Stock */}
                  <div>
                    {tipo === 'ninguno'
                      ? <span style={S.tag('red')}>Sin stock</span>
                      : tipo === 'decant'
                        ? <span style={{ ...S.tag('warn'), fontSize: 9 }}>Solo decant</span>
                        : stockOk && p.stock <= 3
                          ? <span style={{ ...S.tag('warn'), fontSize: 9 }}>{p.stock} ud{p.stock > 1 ? 's' : ''}</span>
                          : <span style={{ ...S.tag('green'), fontSize: 9 }}>{p.stock} ud{p.stock > 1 ? 's' : ''}</span>
                    }
                  </div>

                  {/* Decants */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {d5  > 0 && <span style={{ fontSize: 10, color: '#7c3aed', background: '#f5f3ff', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>5ml {fmtP(d5)}</span>}
                    {d10 > 0 && <span style={{ fontSize: 10, color: '#7c3aed', background: '#f5f3ff', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>10ml {fmtP(d10)}</span>}
                    {!hd && <span style={{ fontSize: 10, color: '#ccc' }}>—</span>}
                  </div>

                </div>
              );
            })}

            {lista.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: '#bbb', fontSize: 13 }}>Sin resultados</div>
            )}
          </div>
        );
      })()}

      {tab === 'perfumes' && subTab === 'stock' && (
        <div style={{ padding: '14px 16px' }}>
          <h3 style={{ color: '#b8860b', marginTop: 0 }}>Gestión de stock</h3>
          <input style={{ ...S.inp, marginBottom: 12 }} placeholder="Buscar perfume..." value={stockSearch} onChange={e => setStockSearch(e.target.value)} />
          {perfumes.filter(p => p.nombre.toLowerCase().includes(stockSearch.toLowerCase()) || p.marca.toLowerCase().includes(stockSearch.toLowerCase())).map(p => {
            const tieneDecant = parseFloat(p.precio_decant_5ml) > 0 || parseFloat(p.precio_decant_10ml) > 0;
            const tipo = p.stock > 0 && tieneDecant ? 'ambos' : p.stock > 0 ? 'perfume' : tieneDecant ? 'decant' : 'ninguno';
            const tipoOpts = [
              { val: 'perfume', label: '🍾 Frasco entero' },
              { val: 'decant',  label: '🧪 Solo decants' },
              { val: 'ambos',   label: '✦ Frasco + decants' },
            ];
            return (
              <div key={p.id} style={{ ...S.card, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {miniImg(p)}
                <div style={{ flex: 1, minWidth: 120 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{p.nombre}</div>
                  <div style={{ fontSize: 11, color: '#aaa', marginBottom: 8 }}>{p.marca} · {p.familia}</div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {tipoOpts.map(opt => (
                      <button key={opt.val}
                        onClick={() => actualizarStock(p.id, opt.val === 'decant' ? 0 : Math.max(p.stock, 1))}
                        style={{
                          padding: '4px 10px', fontSize: 10, fontWeight: 600, borderRadius: 20, cursor: 'pointer',
                          border: tipo === opt.val ? '2px solid #b8860b' : '1px solid #e0e0e0',
                          background: tipo === opt.val ? '#fff9ee' : '#fafafa',
                          color: tipo === opt.val ? '#b8860b' : '#999',
                          letterSpacing: 0.3,
                        }}
                      >{opt.label}</button>
                    ))}
                  </div>
                </div>
                {p.stock > 0 && (
                  <>
                    <button style={{ ...S.btn('red'), padding: '4px 10px', fontSize: 15 }} onClick={() => actualizarStock(p.id, Math.max(1, p.stock - 1))}>-</button>
                    <span style={{ fontWeight: 700, fontSize: 15, minWidth: 28, textAlign: 'center', color: p.stock <= 5 ? '#e53935' : '#2e7d32' }}>{p.stock}</span>
                    <button style={{ ...S.btn('teal'), padding: '4px 10px', fontSize: 15 }} onClick={() => actualizarStock(p.id, p.stock + 1)}>+</button>
                  </>
                )}
                <span style={S.tag(tipo==='ninguno'?'red':tipo==='decant'?'warn':'green')}>
                  {tipo === 'ninguno' ? 'Sin stock' : tipo === 'decant' ? 'Decant' : tipo === 'ambos' ? 'Ambos' : p.stock <= 5 ? 'Bajo' : 'OK'}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* PRECIOS */}
      {tab === 'perfumes' && subTab === 'precios' && (
        <div style={{ padding: '14px 16px' }}>
          <h3 style={{ color: '#b8860b', marginTop: 0 }}>Precios, costos y ganancia</h3>
          <input style={{ ...S.inp, marginBottom: 12 }} placeholder="Buscar perfume..." value={precioSearch} onChange={e => setPrecioSearch(e.target.value)} />
          {perfumes.filter(p => p.nombre.toLowerCase().includes(precioSearch.toLowerCase()) || p.marca.toLowerCase().includes(precioSearch.toLowerCase())).map(p => {
            const precio = parseFloat(p.precio) || 0;
            const costo = parseFloat(p.costo) || 0;
            const precioFinal = p.descuento > 0 ? Math.round(precio * (1 - p.descuento / 100)) : precio;
            const ganancia = precioFinal - costo;
            const margen = precioFinal > 0 ? Math.round((ganancia / precioFinal) * 100) : 0;
            const margenColor = margen >= 40 ? '#1a7a4a' : margen >= 20 ? '#b7600a' : '#b71c1c';
            return (
            <div key={p.id} style={S.card}>
              {/* Fila principal: imagen + nombre + precio */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {miniImg(p)}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{p.nombre}</div>
                  <div style={{ fontSize: 11, color: '#aaa' }}>{p.marca} · {p.familia}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {p.descuento > 0 && <div style={{ fontSize: 11, textDecoration: 'line-through', color: '#aaa' }}>{fmt(precio)}</div>}
                  <div style={{ fontWeight: 700, color: p.descuento > 0 ? '#e53935' : '#b8860b', fontSize: 15 }}>
                    {fmt(precioFinal)}
                  </div>
                  {p.descuento > 0 && <span style={S.tag('red')}>-{p.descuento}%</span>}
                </div>
              </div>

              {/* Fila de costo y ganancia */}
              <div style={{ display: 'flex', gap: 8, marginTop: 8, padding: '8px 10px', background: '#fafaf8', borderRadius: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <div style={{ fontSize: 10, color: '#aaa', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 2 }}>Costo</div>
                  {editandoCosto === p.id ? (
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                      <input type="number" style={{ ...S.inp, padding: '4px 8px', fontSize: 12 }} value={nuevoCosto} onChange={e => setNuevoCosto(e.target.value)} autoFocus />
                      <button style={{ ...S.btn('gold'), padding: '4px 8px', fontSize: 11 }} onClick={() => actualizarCosto(p.id)}>OK</button>
                      <button style={{ ...S.btn(''), padding: '4px 8px', fontSize: 11 }} onClick={() => setEditandoCosto(null)}>X</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: costo > 0 ? '#333' : '#ccc' }}>
                        {costo > 0 ? fmt(costo) : '(sin costo)'}
                      </span>
                      <button
                        style={{ background: 'none', border: '1px solid #d5c9a8', borderRadius: 4, padding: '2px 7px', fontSize: 10, color: '#888', cursor: 'pointer' }}
                        onClick={() => { setEditandoCosto(p.id); setNuevoCosto(String(costo || '')); }}>
                        editar
                      </button>
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', minWidth: 120 }}>
                  <div style={{ fontSize: 10, color: '#aaa', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 2 }}>Ganancia</div>
                  {costo > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: ganancia >= 0 ? margenColor : '#b71c1c' }}>{fmt(ganancia)}</span>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: margenColor + '20', color: margenColor }}>
                        {margen}%
                      </span>
                    </div>
                  ) : (
                    <span style={{ fontSize: 12, color: '#ccc' }}>ingresa el costo</span>
                  )}
                </div>
              </div>

              {/* Botones precio y descuento */}
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {editandoPrecio === p.id ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 }}>
                    <input type="number" style={{ ...S.inp, flex: 1 }} value={nuevoPrecio} onChange={e => setNuevoPrecio(e.target.value)} autoFocus />
                    <button style={{ ...S.btn('gold'), padding: '6px 10px' }} onClick={() => actualizarPrecio(p.id)}>OK</button>
                    <button style={{ ...S.btn(''), padding: '6px 10px' }} onClick={() => setEditandoPrecio(null)}>X</button>
                  </div>
                ) : (
                  <button style={{ ...S.btn(''), padding: '4px 10px', fontSize: 12, border: '1px solid #e8c97a' }}
                    onClick={() => { setEditandoPrecio(p.id); setNuevoPrecio(String(precio)); }}>Precio</button>
                )}
                {editandoDescuento === p.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 2 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Descuento %</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {[0, 5, 10, 15, 20, 25, 30].map(d => (
                        <button key={d} style={{ padding: '4px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, background: nuevoDescuento===d?'#e53935':'#f5f0e8', color: nuevoDescuento===d?'#fff':'#555', fontWeight: nuevoDescuento===d?700:400 }}
                          onClick={() => setNuevoDescuento(d)}>
                          {d === 0 ? 'Sin desc.' : `-${d}%`}
                        </button>
                      ))}
                    </div>
                    {nuevoDescuento > 0 && (
                      <div style={{ background: '#fff8f0', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}>
                        <span style={{ textDecoration: 'line-through', color: '#aaa', marginRight: 8 }}>{fmt(precio)}</span>
                        <span style={{ fontWeight: 700, color: '#e53935', fontSize: 15 }}>{fmt(Math.round(precio*(1-nuevoDescuento/100)))}</span>
                        {costo > 0 && <span style={{ color: '#888', marginLeft: 8 }}>Ganancia: {fmt(Math.round(precio*(1-nuevoDescuento/100)) - costo)}</span>}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={{ ...S.btn('gold'), flex: 1 }} onClick={() => actualizarDescuento(p.id)}>Aplicar</button>
                      <button style={S.btn('')} onClick={() => setEditandoDescuento(null)}>X</button>
                    </div>
                  </div>
                ) : (
                  <button style={{ ...S.btn(p.descuento > 0 ? 'red' : ''), padding: '4px 10px', fontSize: 12, border: '1px solid #e8c97a' }}
                    onClick={() => { setEditandoDescuento(p.id); setNuevoDescuento(p.descuento || 0); }}>
                    {p.descuento > 0 ? `Desc. ${p.descuento}%` : 'Descuento'}
                  </button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* EDITAR PERFUME */}
      {tab === 'perfumes' && subTab === 'editar' && (
        <div style={{ padding: '14px 16px' }}>
          <h3 style={{ color: '#b8860b', marginTop: 0 }}>Editar perfumes</h3>
          <input style={{ ...S.inp, marginBottom: 12 }} placeholder="Buscar perfume..." value={stockSearch} onChange={e => setStockSearch(e.target.value)} />
          {perfumes.filter(p => p.nombre.toLowerCase().includes(stockSearch.toLowerCase()) || p.marca.toLowerCase().includes(stockSearch.toLowerCase())).map(p => (
            <div key={p.id} style={S.card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: editandoPerfume===p.id?10:0 }}>
                {miniImg(p)}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{p.nombre}</div>
                  <div style={{ fontSize: 11, color: '#aaa' }}>{p.marca} · {p.familia} · {p.ml}ml</div>
                </div>
                <button style={{ ...S.btn(editandoPerfume===p.id?'red':''), padding: '5px 10px', fontSize: 12, border: '1px solid #e8c97a' }}
                  onClick={() => {
                    if (editandoPerfume===p.id) { setEditandoPerfume(null); return; }
                    setEditandoPerfume(p.id);
                    setEditPerfumeForm({ nombre:p.nombre, marca:p.marca, precio:p.precio, costo:p.costo, stock:p.stock, ml:p.ml, familia:p.familia, notas:p.notas||'', origen:p.origen||'', imagen_url:p.imagen_url||'', imagen_url_2:p.imagen_url_2||'', imagen_url_3:p.imagen_url_3||'', inspiracion: typeof p.inspiracion === 'string' ? JSON.parse(p.inspiracion||'{}') : (p.inspiracion||{}), precio_decant_5ml: p.precio_decant_5ml||'', precio_decant_10ml: p.precio_decant_10ml||'', imagen_decant: p.imagen_decant||'', tipo_venta: p.tipo_venta||'perfume' });
                  }}>
                  {editandoPerfume===p.id ? 'Cancelar' : 'Editar'}
                </button>
              </div>
              {editandoPerfume === p.id && (
                <div style={{ borderTop: '1px solid #e8c97a', paddingTop: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {[['nombre','Nombre'],['marca','Marca'],['precio','Precio $'],['costo','Costo $'],['stock','Stock'],['ml','ML'],['origen','Origen']].map(([k,lbl]) => (
                      <div key={k} style={{ gridColumn: k==='nombre'?'1/-1':'auto' }}>
                        <label style={S.lbl}>{lbl}</label>
                        <input style={S.inp} value={editPerfumeForm[k]||''} onChange={e => setEditPerfumeForm(f => ({ ...f, [k]: e.target.value }))} />
                      </div>
                    ))}
                    <div>
                      <label style={S.lbl}>Familia</label>
                      <select style={S.inp} value={editPerfumeForm.familia||'Femenino'} onChange={e => setEditPerfumeForm(f => ({ ...f, familia: e.target.value }))}>
                        <option>Femenino</option>
                        <option>Masculino</option>
                        <option>Unisex</option>
                      </select>
                    </div>

                    {/* NOTAS — textarea con Fragrantica */}
                    <div style={{ gridColumn: '1/-1' }}>
                      <label style={S.lbl}>Notas aromáticas</label>
                      <div style={{ display:'flex', gap:6, alignItems:'flex-start' }}>
                        <textarea
                          rows={3}
                          style={{ ...S.inp, flex:1, resize:'vertical', fontFamily:'inherit', fontSize:12 }}
                          placeholder="oud, ámbar, bergamota, almizcle..."
                          value={editPerfumeForm.notas||''}
                          onChange={e => setEditPerfumeForm(f => ({ ...f, notas: e.target.value }))}
                        />
                        <button onClick={() => { setFragPanel(p.id); setFragResultados(null); setFragBusqueda(''); }}
                          title="Buscar notas en Fragrantica"
                          style={{ ...S.btn('teal'), padding:'8px 10px', fontSize:11, whiteSpace:'nowrap', flexShrink:0 }}>
                          🌸 Fragrantica
                        </button>
                      </div>
                      {fragPanel === p.id && (
                        <div style={{ marginTop:8, background:'#f0f7ff', borderRadius:10, padding:12, border:'1px solid #b3d4f7' }}>
                          <div style={{ fontWeight:700, fontSize:12, color:'#1565c0', marginBottom:10, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                            🌸 Notas automáticas — Parfumo
                            <button onClick={() => { setFragPanel(false); setFragResultados(null); setFragBusqueda(''); }} style={{ background:'none', border:'none', cursor:'pointer', color:'#888', fontSize:14 }}>✕</button>
                          </div>
                          {!fragResultados && !fragCargando && (
                            <button
                              onClick={() => buscarEnFragrantica(editPerfumeForm.marca || p.marca, editPerfumeForm.nombre || p.nombre, p.id)}
                              style={{ ...S.btn('teal'), fontSize:12, padding:'9px 0', width:'100%', marginBottom:8 }}>
                              🔍 Buscar notas de "{editPerfumeForm.nombre || p.nombre}"
                            </button>
                          )}
                          {fragCargando && (
                            <div style={{ textAlign:'center', padding:'16px 0', color:'#1565c0', fontSize:12 }}>
                              ⏳ Buscando en Parfumo.net...
                            </div>
                          )}
                          {fragResultados && fragResultados.notas.length > 0 && (
                            <div>
                              <div style={{ fontSize:11, color:'#2e7d32', fontWeight:700, marginBottom:8 }}>
                                ✅ {fragResultados.notas.length} notas encontradas (traducidas al español):
                              </div>
                              <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:10 }}>
                                {fragResultados.notas.map((n,i) => (
                                  <span key={i} style={{ background:'#1a1a1a', color:'#e8c96a', fontSize:11, padding:'3px 10px', borderRadius:10 }}>{n}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {fragResultados && fragResultados.notas.length === 0 && (
                            <div style={{ fontSize:11, color:'#e65100', marginBottom:8 }}>
                              No se encontró automáticamente. Podés buscarlo manualmente:
                              <br/>
                              <a href={fragResultados.url} target="_blank" rel="noreferrer" style={{ color:'#1565c0', fontWeight:700 }}>
                                🔗 Buscar en Parfumo.net
                              </a>
                            </div>
                          )}
                          <div style={{ fontSize:11, color:'#555', marginBottom:5 }}>
                            {fragResultados?.notas.length > 0 ? 'Editá si hace falta:' : 'O pegá las notas manualmente:'}
                          </div>
                          <textarea
                            value={fragBusqueda}
                            onChange={e => setFragBusqueda(e.target.value)}
                            placeholder="oud, ámbar, almizcle, bergamota, vainilla..."
                            rows={2}
                            style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:'1.5px solid #b3d4f7', fontSize:12, resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }}
                          />
                          <div style={{ display:'flex', gap:8, marginTop:8 }}>
                            <button onClick={() => { setFragPanel(false); setFragResultados(null); setFragBusqueda(''); }}
                              style={{ ...S.btn(''), flex:1, fontSize:12 }}>Cancelar</button>
                            <button onClick={() => guardarNotasFragrantica(p.id, fragBusqueda)}
                              disabled={!fragBusqueda.trim()}
                              style={{ ...S.btn('gold'), flex:2, fontSize:12, opacity:fragBusqueda.trim()?1:0.5 }}>
                              ✅ Guardar notas
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* FOTOS 1, 2, 3 con upload */}
                    {[['imagen_url','📷 Foto 1'],['imagen_url_2','📷 Foto 2'],['imagen_url_3','📷 Foto 3']].map(([k,lbl]) => (
                      <div key={k} style={{ gridColumn: '1/-1' }}>
                        <label style={S.lbl}>{lbl}</label>
                        <div style={{ display:'flex', gap:8 }}>
                          <input style={{ ...S.inp, flex:1 }} placeholder="URL o subir imagen" value={editPerfumeForm[k]||''} onChange={e => setEditPerfumeForm(f => ({ ...f, [k]: e.target.value }))} />
                          <label style={{ ...S.btn('teal'), cursor:'pointer', display:'flex', alignItems:'center', gap:4, whiteSpace:'nowrap', padding:'9px 12px' }}>
                            Subir
                            <input type="file" accept="image/*" style={{ display:'none' }} onChange={e => subirFoto(e.target.files[0], url => setEditPerfumeForm(f => ({ ...f, [k]: url })))} />
                          </label>
                        </div>
                        {editPerfumeForm[k] && (
                          <img src={editPerfumeForm[k]} alt="preview" style={{ width:60, height:60, objectFit:'cover', borderRadius:6, marginTop:4, border:'1px solid #e8c97a' }} onError={e => e.target.style.display='none'} />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* ── INSPIRACIÓN ────────────────────────── */}
                  <div style={{ marginTop:14, borderTop:'1px solid #e8c97a', paddingTop:12 }}>
                    <div style={{ fontWeight:700, fontSize:12, color:'#b8860b', marginBottom:10 }}>✨ Inspiración</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      {[
                        { key:'ocasion', label:'Ocasión', color:'#b8860b', opts:['Diario','Trabajo','Salida','Noche','Cita romántica','Ocasión especial'] },
                        { key:'estacion', label:'Estación', color:'#4a90e2', opts:['Primavera 🌸','Verano ☀️','Otoño 🍂','Invierno ❄️','Todas'] },
                        { key:'momento', label:'Momento del día', color:'#7c4dff', opts:['Mañana','Tarde','Noche','Todo el día'] },
                      ].map(({ key, label, color, opts }) => (
                        <div key={key}>
                          <label style={{ ...S.lbl, marginBottom:6 }}>{label}</label>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                            {opts.map(opt => {
                              const sel = (editPerfumeForm.inspiracion?.[key]||[]).includes(opt);
                              return (
                                <button key={opt} type="button" onClick={() => {
                                  const curr = editPerfumeForm.inspiracion?.[key] || [];
                                  const next = sel ? curr.filter(x=>x!==opt) : [...curr,opt];
                                  setEditPerfumeForm(f=>({...f, inspiracion:{...f.inspiracion,[key]:next}}));
                                }} style={{ padding:'4px 11px', borderRadius:10, border:`1.5px solid ${sel?color:'#ddd'}`, background:sel?color:'#fff', color:sel?'#fff':'#666', cursor:'pointer', fontSize:11, fontFamily:'inherit', fontWeight:sel?700:400 }}>
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      {/* Intensidad */}
                      <div>
                        <label style={S.lbl}>Intensidad</label>
                        <div style={{ display:'flex', gap:5 }}>
                          {['Suave','Moderada','Intensa','Muy intensa'].map((op,i) => {
                            const sel = editPerfumeForm.inspiracion?.intensidad === op;
                            const cols = ['#66bb6a','#ffa726','#ef5350','#b71c1c'];
                            return (
                              <button key={op} type="button" onClick={() => setEditPerfumeForm(f=>({...f,inspiracion:{...f.inspiracion,intensidad:op}}))}
                                style={{ flex:1, padding:'5px 0', borderRadius:8, border:`1.5px solid ${sel?cols[i]:'#ddd'}`, background:sel?cols[i]:'#fff', color:sel?'#fff':'#666', cursor:'pointer', fontSize:10, fontFamily:'inherit', fontWeight:sel?700:400 }}>
                                {op}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* FOTO DE INSPIRACIÓN */}
                      <div>
                        <label style={S.lbl}>🖼️ Foto de inspiración</label>
                        <div style={{ display:'flex', gap:8 }}>
                          <input
                            style={{ ...S.inp, flex:1 }}
                            placeholder="URL o subir imagen"
                            value={editPerfumeForm.inspiracion?.foto||''}
                            onChange={e => setEditPerfumeForm(f=>({...f, inspiracion:{...f.inspiracion, foto:e.target.value}}))}
                          />
                          <label style={{ ...S.btn('teal'), cursor:'pointer', display:'flex', alignItems:'center', gap:4, whiteSpace:'nowrap', padding:'9px 12px' }}>
                            Subir
                            <input type="file" accept="image/*" style={{ display:'none' }} onChange={e => subirFoto(e.target.files[0], url => setEditPerfumeForm(f=>({...f, inspiracion:{...f.inspiracion, foto:url}})))} />
                          </label>
                        </div>
                        {editPerfumeForm.inspiracion?.foto && (
                          <img src={editPerfumeForm.inspiracion.foto} alt="inspiracion preview" style={{ width:80, height:80, objectFit:'cover', borderRadius:8, marginTop:6, border:'1px solid #e8c97a' }} onError={e => e.target.style.display='none'} />
                        )}
                      </div>
                    </div>
                  </div>


                  {/* ── DECANTS ─────────────────────────── */}
                  <div style={{ marginTop:14, borderTop:'1px solid #e8c97a', paddingTop:12 }}>
                    <div style={{ fontWeight:700, fontSize:12, color:'#b8860b', marginBottom:10 }}>🧪 Decants</div>
                    {/* Tipo de venta */}
                    <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap' }}>
                      {[
                        { val:'perfume', label:'Solo perfume', emoji:'🡺' },
                        { val:'decant',  label:'Solo decant',  emoji:'🧪' },
                        { val:'ambos',   label:'Perfume + decant', emoji:'✦' },
                      ].map(opt => (
                        <button key={opt.val}
                          onClick={() => setEditPerfumeForm(f=>({...f, tipo_venta: opt.val}))}
                          style={{
                            padding:'6px 12px', fontSize:10, fontWeight:600, borderRadius:2, cursor:'pointer',
                            border: editPerfumeForm.tipo_venta===opt.val ? '2px solid #b8860b' : '1px solid #e0e0e0',
                            background: editPerfumeForm.tipo_venta===opt.val ? '#fff9ee' : '#fafafa',
                            color: editPerfumeForm.tipo_venta===opt.val ? '#b8860b' : '#888',
                            letterSpacing:0.5,
                          }}>
                          {opt.emoji} {opt.label}
                        </button>
                      ))}
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                      <div>
                        <label style={S.lbl}>Precio 5ml</label>
                        <input style={S.inp} type="number" placeholder="0" value={editPerfumeForm.precio_decant_5ml||''} onChange={e => setEditPerfumeForm(f=>({...f, precio_decant_5ml: e.target.value}))} />
                      </div>
                      <div>
                        <label style={S.lbl}>Precio 10ml</label>
                        <input style={S.inp} type="number" placeholder="0" value={editPerfumeForm.precio_decant_10ml||''} onChange={e => setEditPerfumeForm(f=>({...f, precio_decant_10ml: e.target.value}))} />
                      </div>
                    </div>
                    <div style={{ marginTop:10 }}>
                      <label style={S.lbl}>📷 Foto del decant</label>
                      <div style={{ display:'flex', gap:8 }}>
                        <input style={{ ...S.inp, flex:1 }} placeholder="URL o subir imagen" value={editPerfumeForm.imagen_decant||''} onChange={e => setEditPerfumeForm(f=>({...f, imagen_decant: e.target.value}))} />
                        <label style={{ ...S.btn('teal'), cursor:'pointer', display:'flex', alignItems:'center', gap:4, whiteSpace:'nowrap', padding:'9px 12px' }}>
                          Subir
                          <input type="file" accept="image/*" style={{ display:'none' }} onChange={e => subirFoto(e.target.files[0], url => setEditPerfumeForm(f=>({...f, imagen_decant: url})))} />
                        </label>
                      </div>
                      {editPerfumeForm.imagen_decant && (
                        <img src={editPerfumeForm.imagen_decant} alt="decant preview" style={{ width:60, height:60, objectFit:'cover', borderRadius:6, marginTop:4, border:'1px solid #e8c97a' }} onError={e => e.target.style.display='none'} />
                      )}
                    </div>
                    {(!editPerfumeForm.precio_decant_5ml && !editPerfumeForm.precio_decant_10ml) && (
                      <div style={{ fontSize:10, color:'#bbb', marginTop:8, fontStyle:'italic' }}>Dejá los precios en 0 si no tenés decants de este perfume.</div>
                    )}
                  </div>
                  <button style={{ ...S.btn('gold'), width: '100%', marginTop: 12 }} onClick={() => guardarEdicionPerfume(p.id)}>
                    Guardar cambios
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* AGREGAR PERFUME */}
      {tab === 'perfumes' && subTab === 'agregar' && (
        <div style={{ padding: '14px 16px' }}>
          <h3 style={{ color: '#b8860b', marginTop: 0 }}>Agregar nuevo perfume</h3>
          <div style={S.card}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[['nombre','Nombre'],['marca','Marca'],['precio','Precio $'],['costo','Costo $'],['stock','Stock'],['ml','ML'],['notas','Notas aromaticas'],['origen','Origen']].map(([k,lbl]) => (
                <div key={k} style={{ gridColumn: k==='nombre'||k==='notas'?'1/-1':'auto' }}>
                  <label style={S.lbl}>{lbl}</label>
                  <input style={S.inp} value={nuevoPerfumeForm[k]} onChange={e => setNuevoPerfumeForm(f => ({ ...f, [k]: e.target.value }))} />
                </div>
              ))}
              <div>
                <label style={S.lbl}>Familia</label>
                <select style={S.inp} value={nuevoPerfumeForm.familia} onChange={e => setNuevoPerfumeForm(f => ({ ...f, familia: e.target.value }))}>
                  <option>Femenino</option>
                  <option>Masculino</option>
                  <option>Unisex</option>
                </select>
              </div>
              {[['imagen_url','Foto 1'],['imagen_url_2','Foto 2'],['imagen_url_3','Foto 3']].map(([k,lbl]) => (
                <div key={k} style={{ gridColumn: '1/-1' }}>
                  <label style={S.lbl}>{lbl}</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input style={{ ...S.inp, flex: 1 }} placeholder="URL o subir imagen" value={nuevoPerfumeForm[k]} onChange={e => setNuevoPerfumeForm(f => ({ ...f, [k]: e.target.value }))} />
                    <label style={{ ...S.btn('teal'), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', padding: '9px 12px' }}>
                      Subir
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => subirFoto(e.target.files[0], url => setNuevoPerfumeForm(f => ({ ...f, [k]: url })))} />
                    </label>
                  </div>
                  {nuevoPerfumeForm[k] && (
                    <img src={nuevoPerfumeForm[k]} alt="preview" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, marginTop: 4, border: '1px solid #e8c97a' }} onError={e => e.target.style.display='none'} />
                  )}
                </div>
              ))}
            </div>
            {nuevoPerfumeForm.costo && (
              <div style={{ marginTop: 14, background: '#fdf9f0', borderRadius: 10, padding: 14, border: '1px solid #e8c97a' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#b8860b', marginBottom: 10 }}>Calculadora de precio</div>
                <div style={{ marginBottom: 8 }}>
                  <label style={S.lbl}>Margen sobre precio de venta: {calcMargin}%</label>
                  <input type="range" min={10} max={60} step={1} value={calcMargin} onChange={e => setCalcMargin(parseInt(e.target.value))} style={{ width: '100%' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                  {[35, 45, 70, 80].map(m => (
                    <div key={m} style={{ background: m===calcMargin?'#b8860b':'#f5f0e8', borderRadius: 8, padding: '6px', textAlign: 'center', cursor: 'pointer', color: m===calcMargin?'#fff':'#555', fontSize: 12 }} onClick={() => setCalcMargin(m)}>
                      <div style={{ fontWeight: 700 }}>{m}%</div>
                      <div style={{ fontSize: 11 }}>{fmt(parseFloat(nuevoPerfumeForm.costo)/(1-m/100))}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#aaa' }}>Precio de venta sugerido</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#b8860b' }}>{fmt(parseFloat(nuevoPerfumeForm.costo)/(1-calcMargin/100))}</div>
                    <div style={{ fontSize: 11, color: '#aaa' }}>Ganancia: {fmt(parseFloat(nuevoPerfumeForm.costo)/(1-calcMargin/100)*calcMargin/100)} ({calcMargin}% sobre venta)</div>
                  </div>
                  <button style={{ ...S.btn('teal'), fontSize: 12 }} onClick={() => setNuevoPerfumeForm(f => ({ ...f, precio: String(Math.round(parseFloat(f.costo)/(1-calcMargin/100))) }))}>
                    Usar este precio
                  </button>
                </div>
                <div style={{ marginTop: 12, borderTop: '1px solid #e8c97a', paddingTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8 }}>Precio con descuento</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6 }}>
                    {[5,10,15,20].map(d => {
                      const base = parseFloat(nuevoPerfumeForm.costo)/(1-calcMargin/100);
                      const desc = base*(1-d/100);
                      const gan = desc - parseFloat(nuevoPerfumeForm.costo);
                      return (
                        <div key={d} style={{ background: '#fff8f0', borderRadius: 8, padding: '6px', textAlign: 'center', border: '1px solid #e8c97a' }}>
                          <div style={{ fontWeight: 700, fontSize: 12, color: '#e65100' }}>-{d}%</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#b8860b' }}>{fmt(desc)}</div>
                          <div style={{ fontSize: 10, color: gan>0?'#2e7d32':'#e53935' }}>{gan>0?fmt(gan):'Perdida'}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            <button style={{ ...S.btn('gold'), width: '100%', marginTop: 14 }} onClick={agregarPerfume}>
              Agregar perfume
            </button>
          </div>
        </div>
      )}

      {/* FOTOS */}
      {tab === 'perfumes' && subTab === 'fotos' && (
        <div style={{ padding: '14px 16px' }}>
          <h3 style={{ color: '#b8860b', marginTop: 0 }}>Gestionar fotos</h3>
          <div style={{ background: '#e8f5e9', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#2e7d32', marginBottom: 12 }}>
            Pega una URL o usa el boton Subir para cargar una foto desde tu dispositivo.
          </div>
          {perfumes.map(p => (
            <div key={p.id} style={{ ...S.card, display: 'flex', alignItems: 'center', gap: 10 }}>
              {miniImg(p)}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{p.nombre}</div>
                <div style={{ fontSize: 11, color: '#aaa' }}>{p.marca}</div>
              </div>
              {editandoFoto === p.id ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 2 }}>
                  {[['imagen_url','Foto principal'],['imagen_url_2','Foto 2'],['imagen_url_3','Foto 3']].map(([key, lbl]) => (
                    <div key={key}>
                      <label style={S.lbl}>{lbl}</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input
                          style={{ ...S.inp, flex: 1 }}
                          placeholder="URL o subir imagen"
                          value={editandoFotoUrls[key]||''}
                          onChange={e => setEditandoFotoUrls(u => ({ ...u, [key]: e.target.value }))}
                        />
                        <label style={{ ...S.btn('teal'), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', padding: '9px 12px' }}>
                          Subir
                          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => subirFoto(e.target.files[0], url => setEditandoFotoUrls(u => ({ ...u, [key]: url })))} />
                        </label>
                      </div>
                      {editandoFotoUrls?.[key] && <img src={editandoFotoUrls[key]} alt="preview" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, marginTop: 4, border: '1px solid #e8c97a' }} onError={e => e.target.style.display='none'} />}
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={{ ...S.btn('gold'), flex: 1 }} onClick={() => actualizarFotos(p.id)}>Guardar</button>
                    <button style={S.btn('')} onClick={() => setEditandoFoto(null)}>X</button>
                  </div>
                </div>
              ) : (
                <button style={{ ...S.btn(''), padding: '4px 10px', fontSize: 12, border: '1px solid #e8c97a' }}
                  onClick={() => { setEditandoFoto(p.id); setEditandoFotoUrls({ imagen_url: p.imagen_url||'', imagen_url_2: p.imagen_url_2||'', imagen_url_3: p.imagen_url_3||'' }); }}>
                  {p.imagen_url ? 'Cambiar fotos' : 'Agregar fotos'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* NUEVA VENTA */}
      {tab === 'ventas' && (
        <div style={{ padding: '20px 24px', maxWidth: 900, margin: '0 auto' }}>

          {/* REGISTRAR VENTA */}
          <div style={{ fontSize: 10, fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 14 }}>Registrar venta</div>

          <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 2, padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={S.lbl}>Cliente</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select style={{ ...S.inp, flex: 1 }} value={ventaForm.clienteId} onChange={e => setVentaForm(f => ({ ...f, clienteId: e.target.value }))}>
                    <option value="">Seleccionar cliente...</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}{c.dni ? ` · ${c.dni}` : ''}</option>)}
                  </select>
                  <button style={{ ...S.btn('teal'), whiteSpace: 'nowrap', fontSize: 12 }} onClick={() => setMostrarNuevoCliente(!mostrarNuevoCliente)}>+ Nuevo</button>
                </div>

                {mostrarNuevoCliente && (
                  <div style={{ marginTop: 10, background: '#f9f9f7', borderRadius: 2, padding: 14, border: '1px solid #e0e0e0' }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 10 }}>Nuevo cliente</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div style={{ gridColumn: '1/-1' }}>
                        <label style={S.lbl}>Nombre *</label>
                        <input style={S.inp} placeholder="Nombre completo" value={nuevoClienteForm.nombre} onChange={e => setNuevoClienteForm(f => ({ ...f, nombre: e.target.value }))} />
                      </div>
                      <div>
                        <label style={S.lbl}>Teléfono</label>
                        <input style={S.inp} placeholder="Teléfono" value={nuevoClienteForm.telefono} onChange={e => setNuevoClienteForm(f => ({ ...f, telefono: e.target.value }))} />
                      </div>
                      <div>
                        <label style={S.lbl}>DNI / CUIL / CUIT</label>
                        <input style={S.inp} placeholder="20-12345678-9" value={nuevoClienteForm.dni} onChange={e => setNuevoClienteForm(f => ({ ...f, dni: e.target.value }))} />
                      </div>
                      <div style={{ gridColumn: '1/-1' }}>
                        <label style={S.lbl}>Email</label>
                        <input style={S.inp} placeholder="Email (opcional)" value={nuevoClienteForm.email} onChange={e => setNuevoClienteForm(f => ({ ...f, email: e.target.value }))} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button style={{ ...S.btn('gold'), flex: 2, fontSize: 12 }} onClick={agregarClienteDesdeVenta}>
                        Guardar y continuar venta
                      </button>
                      <button style={{ ...S.btn(''), flex: 1, fontSize: 12 }} onClick={() => setMostrarNuevoCliente(false)}>Cancelar</button>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label style={S.lbl}>Fecha</label>
                <input type="date" style={S.inp} value={ventaForm.date} onChange={e => setVentaForm(f => ({ ...f, date: e.target.value }))} />
              </div>
              <div>
                <label style={S.lbl}>Forma de pago</label>
                <select style={S.inp} value={ventaForm.method} onChange={e => setVentaForm(f => ({ ...f, method: e.target.value }))}>
                  <option>Efectivo</option>
                  <option>Transferencia</option>
                  <option>Cuenta corriente</option>
                  <option>Tarjeta</option>
                </select>
              </div>
            </div>
          </div>

          {/* EXTRAS — decants, regalos, envío, etc. */}
          <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 2, padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: extrasVenta.length > 0 ? 10 : 0 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: 2 }}>Extras (decants, regalos, envío...)</div>
              <button onClick={agregarExtra} style={{ ...S.btn('teal'), padding: '5px 12px', fontSize: 11 }}>+ Agregar extra</button>
            </div>
            {extrasVenta.map(ex => (
              <div key={ex.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <input
                  style={{ ...S.inp, flex: 2, marginBottom: 0 }}
                  placeholder="Descripción (ej: Decant Bleu de Chanel 5ml)"
                  value={ex.desc}
                  onChange={e => actualizarExtra(ex.id, 'desc', e.target.value)}
                />
                <input
                  type="number"
                  style={{ ...S.inp, flex: 1, marginBottom: 0 }}
                  placeholder="Precio $"
                  value={ex.precio}
                  onChange={e => actualizarExtra(ex.id, 'precio', e.target.value)}
                />
                <button onClick={() => eliminarExtra(ex.id)} style={{ background:'none', border:'1px solid #ffcdd2', borderRadius:2, color:'#e53935', cursor:'pointer', padding:'8px 10px', fontSize:12, flexShrink: 0 }}>✕</button>
              </div>
            ))}
          </div>

          {/* SELECTOR DE PERFUMES */}
          <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 2, padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: 2 }}>Elegir perfumes</div>
              {selProds.length > 0 && (
                <div style={{ fontSize: 12, color: '#c9a96e', fontWeight: 600 }}>{selProds.length} seleccionado{selProds.length > 1 ? 's' : ''}</div>
              )}
            </div>
            <input style={{ ...S.inp, marginBottom: 8 }} placeholder="Buscar perfume..." value={ventaSearch} onChange={e => setVentaSearch(e.target.value)} />
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              {['Todos','Femenino','Masculino'].map(f => (
                <button key={f} style={{ padding: '5px 12px', borderRadius: 2, border: '1px solid', borderColor: ventaFiltro===f?'#111':'#e0e0e0', cursor: 'pointer', background: ventaFiltro===f?'#111':'transparent', color: ventaFiltro===f?'#fff':'#888', fontSize: 11, letterSpacing: 0.5 }} onClick={() => setVentaFiltro(f)}>
                  {f}
                </button>
              ))}
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {filtradosVenta.map(p => {
                const sel = selProds.find(x => x.id === p.id);
                return (
                  <div key={p.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 2, background: sel ? '#fafaf5' : '#fff', border: `1px solid ${sel ? '#c9a96e' : '#ececec'}`, cursor: 'pointer', transition: 'all 0.15s' }}
                    onClick={() => toggleProd(p)}>
                    <div style={{ width: 36, height: 36, borderRadius: 2, overflow: 'hidden', background: '#f5f3ef', flexShrink: 0 }}>
                      {p.imagen_url ? <img src={p.imagen_url} alt={p.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display='none'} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#c9a96e' }}>✦</div>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: sel ? 600 : 400, color: '#111' }}>{p.nombre}</div>
                      <div style={{ fontSize: 10, color: '#bbb' }}>{p.marca} · Stock: {p.stock}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {p.descuento > 0 && <div style={{ fontSize: 10, textDecoration: 'line-through', color: '#bbb' }}>{fmt(p.precio)}</div>}
                      <div style={{ fontSize: 13, fontWeight: 600, color: p.descuento > 0 ? '#e53935' : (sel ? '#c9a96e' : '#888') }}>{fmt(p.descuento > 0 ? Math.round(p.precio * (1 - p.descuento / 100)) : p.precio)}</div>
                      {p.descuento > 0 && <div style={{ fontSize: 10, color: '#e53935' }}>-{p.descuento}%</div>}
                    </div>
                    {sel && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>
                        <button style={{ width: 24, height: 24, border: '1px solid #e0e0e0', borderRadius: 2, background: '#fff', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }} onClick={() => updQty(p.id, sel.qty-1)}>−</button>
                        <span style={{ minWidth: 22, textAlign: 'center', fontWeight: 600, fontSize: 13 }}>{sel.qty}</span>
                        <button style={{ width: 24, height: 24, border: '1px solid #e0e0e0', borderRadius: 2, background: '#fff', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }} onClick={() => updQty(p.id, sel.qty+1)}>+</button>
                      </div>
                    )}
                    {sel && <span style={{ fontSize: 12, color: '#c9a96e' }}>✓</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* RESUMEN Y CONFIRMAR */}
          {(selProds.length > 0 || extrasVenta.some(e => e.desc && e.precio)) && (
            <div style={{ background: '#fff', border: '1px solid #c9a96e', borderRadius: 2, padding: 16, marginBottom: 20 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 10 }}>Resumen</div>
              {selProds.map(p => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
                  <span style={{ color: '#111' }}>{p.nombre} <span style={{ color: '#bbb' }}>×{p.qty}</span></span>
                  <span style={{ fontWeight: 600, color: '#111' }}>{fmt(p.precio*p.qty)}</span>
                </div>
              ))}
              {extrasVenta.filter(e => e.desc && e.precio).map(e => (
                <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
                  <span style={{ color: '#111' }}>{e.desc} <span style={{ fontSize: 10, color: '#c9a96e', letterSpacing: 0.5 }}>EXTRA</span></span>
                  <span style={{ fontWeight: 600, color: '#111' }}>{fmt(e.precio)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: 15, marginTop: 10, paddingTop: 8, borderTop: '1px solid #ececec' }}>
                <span style={{ color: '#111' }}>Total</span>
                <span style={{ color: '#c9a96e', fontFamily: "'Cormorant Garamond',serif", fontSize: 18 }}>{fmt(totalVenta)}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, marginTop: 12 }}>
                <div>
                  <label style={S.lbl}>Pago $</label>
                  <input type="number" style={S.inp} placeholder={fmt(totalVenta)} value={ventaForm.paid} onChange={e => setVentaForm(f => ({ ...f, paid: e.target.value }))} />
                </div>
                {ventaForm.paid && Number(ventaForm.paid) < totalVenta && (
                  <div style={{ background: '#FFF8F0', border: '1px solid #FFCC80', borderRadius: 2, padding: '8px 12px', fontSize: 12, color: '#854F0B' }}>
                    Saldo pendiente: {fmt(Math.max(0, totalVenta - parseFloat(ventaForm.paid)))}
                  </div>
                )}
              </div>
              <button style={{ ...S.btn('gold'), width: '100%', marginTop: 12 }} onClick={confirmarVenta}>Confirmar venta</button>
            </div>
          )}

          {/* HISTORIAL AGRUPADO POR CLIENTE */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: 2 }}>Historial de ventas por cliente</div>
            <input style={{ ...S.inp, maxWidth: 220, marginBottom: 0 }} placeholder="Buscar cliente..." value={ventaHistSearch} onChange={e => setVentaHistSearch(e.target.value)} />
          </div>
          {(() => {
            // Agrupar ventas por cliente
            const grupos = {};
            ventas.forEach(v => {
              const key = v.cliente_id || v.cliente_nombre;
              if (!grupos[key]) grupos[key] = { cliente_nombre: v.cliente_nombre, cliente_id: v.cliente_id, ventas: [] };
              grupos[key].ventas.push(v);
            });
            let lista = Object.values(grupos);
            if (ventaHistSearch) {
              lista = lista.filter(g => (g.cliente_nombre||'').toLowerCase().includes(ventaHistSearch.toLowerCase()));
            }
            // Ordenar: los que tienen saldo pendiente primero, luego por fecha de venta más reciente
            lista.sort((a, b) => {
              const deudaA = a.ventas.reduce((s,v)=>s+parseFloat(v.saldo||0),0);
              const deudaB = b.ventas.reduce((s,v)=>s+parseFloat(v.saldo||0),0);
              if ((deudaA>0) !== (deudaB>0)) return deudaB>0 ? 1 : -1;
              const fechaA = a.ventas.map(v=>v.fecha).filter(Boolean).sort().reverse()[0] || '';
              const fechaB = b.ventas.map(v=>v.fecha).filter(Boolean).sort().reverse()[0] || '';
              return fechaB.localeCompare(fechaA);
            });

            if (lista.length === 0) {
              return <div style={{ color: '#ccc', fontSize: 12, textAlign: 'center', padding: '20px 0', letterSpacing: 1 }}>Sin resultados</div>;
            }

            return lista.map(g => {
              const deudaTotal = g.ventas.reduce((s,v)=>s+parseFloat(v.saldo||0),0);
              const totalCliente = g.ventas.reduce((s,v)=>s+parseFloat(v.total||0),0);
              const open = openVentaCliente === g.cliente_id;
              const ventasOrdenadas = [...g.ventas].sort((a,b) => (b.fecha||'').localeCompare(a.fecha||''));
              return (
                <div key={g.cliente_id || g.cliente_nombre} style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 2, marginBottom: 8, overflow: 'hidden' }}>
                  <div
                    onClick={() => setOpenVentaCliente(open ? null : g.cliente_id)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', cursor: 'pointer' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 600, fontSize: 16, color: '#111' }}>{g.cliente_nombre}</span>
                      <span style={{ fontSize: 10, color: '#bbb', background: '#f5f5f3', padding: '2px 8px', borderRadius: 2 }}>{g.ventas.length} venta{g.ventas.length>1?'s':''}</span>
                      {deudaTotal > 0 && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 2, background: '#FAEEDA', color: '#854F0B' }}>Debe {fmt(deudaTotal)}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 600, fontSize: 16, color: '#111' }}>{fmt(totalCliente)}</span>
                      <span style={{ color: '#bbb', fontSize: 12 }}>{open ? '▲' : '▼'}</span>
                    </div>
                  </div>
                  {open && (
                    <div style={{ padding: '0 14px 14px', borderTop: '1px solid #f5f5f5' }}>
                      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {ventasOrdenadas.map(v => (
                          <VentaRow key={v.id} v={v}
                            editandoFormaPago={editandoFormaPago} setEditandoFormaPago={setEditandoFormaPago}
                            nuevaFormaPago={nuevaFormaPago} setNuevaFormaPago={setNuevaFormaPago} guardarFormaPago={guardarFormaPago}
                            imprimirRecibo={imprimirRecibo} setConfirmDelete={setConfirmDelete}
                            payingVenta={payingVenta} setPayingVenta={setPayingVenta}
                            payAmount={payAmount} setPayAmount={setPayAmount}
                            payMethod={payMethod} setPayMethod={setPayMethod}
                            registrarPago={registrarPago} fmt={fmt} S={S} noMargin />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            });
          })()}
          {ventas.length === 0 && (
            <div style={{ color: '#ccc', fontSize: 12, textAlign: 'center', padding: '20px 0', letterSpacing: 1 }}>Sin ventas registradas</div>
          )}
        </div>
      )}

      {/* CLIENTES */}
      {tab === 'clientes' && (
        <div style={{ padding: '20px 24px', maxWidth: 900, margin: '0 auto' }}>

          <div style={{ fontSize: 10, fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 14 }}>Base de clientes · Consulta</div>

          {/* BUSQUEDA + FILTROS */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input style={{ ...S.inp, flex: 1, marginBottom: 0 }} placeholder="Buscar por nombre..." value={clienteSearch} onChange={e => setClienteSearch(e.target.value)} />
            <button
              onClick={() => setClienteFiltrosAbiertos(v => !v)}
              style={{ padding: '8px 16px', borderRadius: 2, border: '1px solid', borderColor: clienteFiltrosAbiertos ? '#111' : '#e0e0e0', background: clienteFiltrosAbiertos ? '#111' : '#fff', color: clienteFiltrosAbiertos ? '#fff' : '#888', fontWeight: 600, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: 1, textTransform: 'uppercase' }}
            >
              {clienteFiltrosAbiertos ? '✕ Filtros' : 'Filtros'}
            </button>
          </div>

          {/* PANEL DE FILTROS */}
          {clienteFiltrosAbiertos && (
            <div style={{ background: '#f9f8f6', border: '1px solid #ececec', borderRadius: 2, padding: '14px 16px', marginBottom: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={{ ...S.lbl }}>Fecha de venta desde</label>
                  <input type="date" style={S.inp} value={clienteFechaDesde} onChange={e => setClienteFechaDesde(e.target.value)} />
                </div>
                <div>
                  <label style={{ ...S.lbl }}>Fecha de venta hasta</label>
                  <input type="date" style={S.inp} value={clienteFechaHasta} onChange={e => setClienteFechaHasta(e.target.value)} />
                </div>
                <div>
                  <label style={{ ...S.lbl }}>Compraron el perfume</label>
                  <input style={S.inp} placeholder="Ej: One Million..." value={clientePerfumeFiltro} onChange={e => setClientePerfumeFiltro(e.target.value)} />
                </div>
                <div>
                  <label style={{ ...S.lbl }}>Deuda mínima $</label>
                  <input type="number" style={S.inp} placeholder="0" value={clienteDeudaMin} onChange={e => setClienteDeudaMin(e.target.value)} />
                </div>
                <div>
                  <label style={{ ...S.lbl }}>Inactivos (sin compras hace)</label>
                  <select style={S.inp} value={clienteInactivosDias} onChange={e => setClienteInactivosDias(e.target.value)}>
                    <option value="">Todos</option>
                    <option value="30">+30 días</option>
                    <option value="60">+60 días</option>
                    <option value="90">+90 días</option>
                    <option value="180">+180 días</option>
                  </select>
                </div>
                <div>
                  <label style={{ ...S.lbl }}>Forma de pago</label>
                  <select style={S.inp} value={clienteFormaPago} onChange={e => setClienteFormaPago(e.target.value)}>
                    <option value="">Todas</option>
                    <option value="Efectivo">Efectivo</option>
                    <option value="Transferencia">Transferencia</option>
                    <option value="Tarjeta">Tarjeta</option>
                    <option value="Cuenta corriente">Cuenta corriente</option>
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 0' }}>
                    <input type="checkbox" checked={clienteSoloDeuda} onChange={e => setClienteSoloDeuda(e.target.checked)} style={{ width: 16, height: 16, accentColor: '#111' }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>Solo con deuda</span>
                  </label>
                </div>
              </div>
              <button
                onClick={() => { setClienteSearch(''); setClienteFechaDesde(''); setClienteFechaHasta(''); setClientePerfumeFiltro(''); setClienteDeudaMin(''); setClienteSoloDeuda(false); setClienteInactivosDias(''); setClienteFormaPago(''); }}
                style={{ fontSize: 11, color: '#c9a96e', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, letterSpacing: 0.5 }}
              >
                Limpiar filtros
              </button>
            </div>
          )}

          {/* LISTA FILTRADA Y ORDENADA */}
          {(() => {
            const hoy = new Date();
            const clientesFiltrados = clientes
              .map(c => {
                const vc = ventas.filter(v => v.cliente_id === c.id);
                const deuda = vc.reduce((a,v) => a + parseFloat(v.saldo||0), 0);
                const total = vc.reduce((a,v) => a + parseFloat(v.total||0), 0);
                const fechas = vc.map(v => v.fecha).filter(Boolean).sort().reverse();
                const ultimaFecha = fechas[0] || null;
                const vcFiltradas = vc.filter(v => {
                  if (clienteFormaPago && v.forma_pago !== clienteFormaPago) return false;
                  const f = v.fecha?.split('T')[0];
                  if (clienteFechaDesde && f < clienteFechaDesde) return false;
                  if (clienteFechaHasta && f > clienteFechaHasta) return false;
                  return true;
                });
                const totalFiltrado = vcFiltradas.reduce((a,v) => a + parseFloat(v.total||0), 0);
                const hayFiltroActivo = clienteFormaPago || clienteFechaDesde || clienteFechaHasta;
                return { ...c, _vc: vc, _deuda: deuda, _total: total, _ultimaFecha: ultimaFecha, _totalFiltrado: totalFiltrado, _hayFiltro: hayFiltroActivo };
              })
              .filter(c => {
                if (clienteSearch && !c.nombre.toLowerCase().includes(clienteSearch.toLowerCase())) return false;
                if (clienteSoloDeuda && c._deuda <= 0) return false;
                if (clienteDeudaMin && c._deuda < parseFloat(clienteDeudaMin)) return false;
                if (clientePerfumeFiltro) {
                  const tiene = c._vc.some(v => v.items?.some(i => i.nombre?.toLowerCase().includes(clientePerfumeFiltro.toLowerCase())));
                  if (!tiene) return false;
                }
                if (clienteFechaDesde || clienteFechaHasta) {
                  const tieneVenta = c._vc.some(v => {
                    if (!v.fecha) return false;
                    const f = v.fecha.split('T')[0];
                    if (clienteFechaDesde && f < clienteFechaDesde) return false;
                    if (clienteFechaHasta && f > clienteFechaHasta) return false;
                    return true;
                  });
                  if (!tieneVenta) return false;
                }
                if (clienteInactivosDias) {
                  const diasLimite = parseInt(clienteInactivosDias);
                  if (!c._ultimaFecha) return true;
                  const diasDesdeUltima = Math.floor((hoy - new Date(c._ultimaFecha)) / (1000*60*60*24));
                  if (diasDesdeUltima < diasLimite) return false;
                }
                if (clienteFormaPago) {
                  const tieneFormaPago = c._vc.some(v => {
                    if (v.forma_pago !== clienteFormaPago) return false;
                    if (clienteFechaDesde || clienteFechaHasta) {
                      const f = v.fecha?.split('T')[0];
                      if (!f) return false;
                      if (clienteFechaDesde && f < clienteFechaDesde) return false;
                      if (clienteFechaHasta && f > clienteFechaHasta) return false;
                    }
                    return true;
                  });
                  if (!tieneFormaPago) return false;
                }
                return true;
              })
              .sort((a, b) => {
                if (!a._ultimaFecha && !b._ultimaFecha) return a.nombre.localeCompare(b.nombre);
                if (!a._ultimaFecha) return 1;
                if (!b._ultimaFecha) return -1;
                return new Date(b._ultimaFecha) - new Date(a._ultimaFecha);
              });

            return (
              <div>
                <div style={{ fontSize: 11, color: '#bbb', marginBottom: 12, letterSpacing: 0.5 }}>
                  {clientesFiltrados.length} cliente{clientesFiltrados.length !== 1 ? 's' : ''}
                  {clientesFiltrados.length !== clientes.length ? ` de ${clientes.length}` : ''}
                </div>
                {clientesFiltrados.map(c => {
                  const open = openCliente === c.id;
                  const ventasCliente = c._vc;
                  const deuda = c._deuda;
                  const total = c._total;
                  return (
                    <div key={c.id} style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 2, padding: '14px 16px', marginBottom: 8, cursor: 'pointer' }} onClick={() => setOpenCliente(open?null:c.id)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 600, fontSize: 17, color: '#111' }}>{c.nombre}</div>
                          <div style={{ fontSize: 11, color: '#bbb', marginTop: 2 }}>
                            {c.telefono || '—'}{c.dni ? ` · ${c.dni}` : ''}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          {c._hayFiltro ? (
                            <>
                              <div style={{ fontSize: 10, color: '#bbb', textTransform: 'uppercase', letterSpacing: 1 }}>En el período</div>
                              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 600, fontSize: 18, color: '#111' }}>{fmt(c._totalFiltrado)}</div>
                              <div style={{ fontSize: 10, color: '#ccc' }}>Histórico: {fmt(total)}</div>
                            </>
                          ) : (
                            <>
                              <div style={{ fontSize: 10, color: '#bbb', textTransform: 'uppercase', letterSpacing: 1 }}>Total comprado</div>
                              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 600, fontSize: 18, color: '#111' }}>{fmt(total)}</div>
                            </>
                          )}
                          {deuda > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 2, background: '#FAEEDA', color: '#854F0B', marginTop: 4, display: 'inline-block' }}>
                              Debe {fmt(deuda)}
                            </span>
                          )}
                        </div>
                      </div>

                      {open && (
                        <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                          {ventasCliente.length === 0 && <div style={{ color: '#bbb', fontSize: 12, textAlign: 'center', padding: '10px 0' }}>Sin compras registradas</div>}
                          {ventasCliente.map(v => (
                            <div key={v.id} style={{ background: '#f9f8f6', borderRadius: 2, padding: '10px 12px', marginBottom: 6, fontSize: 12, border: '1px solid #ececec' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <span style={{ fontWeight: 600, color: '#111' }}>{v.fecha?.split('T')[0]}</span>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                  <span style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 600, fontSize: 14, color: '#111' }}>{fmt(v.total)}</span>
                                  <button onClick={e => { e.stopPropagation(); imprimirRecibo(v); }} title="Imprimir recibo" style={{ background:'none', border:'1px solid #e0e0e0', borderRadius:2, color:'#888', cursor:'pointer', padding:'2px 7px', fontSize:11 }}>🖨</button>
                                </div>
                              </div>
                              <div style={{ color: '#888', marginBottom: 6 }}>{v.items?.filter(i=>i.nombre).map(i=>i.nombre).join(', ')}</div>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 2, background: '#EAF3DE', color: '#3B6D11' }}>Pago {fmt(v.pagado)}</span>
                                {parseFloat(v.saldo)>0 && <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 2, background: '#FAEEDA', color: '#854F0B' }}>Debe {fmt(v.saldo)}</span>}
                                {parseFloat(v.saldo)===0 && <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 2, background: '#EAF3DE', color: '#3B6D11' }}>Saldado</span>}
                                <span style={{ fontSize: 10, color: '#bbb' }}>{v.forma_pago}</span>
                                {parseFloat(v.saldo)>0 && (
                                  <span style={{ fontSize: 10, color: '#c9a96e', marginLeft: 'auto', fontStyle: 'italic' }}>
                                    Registrar pago desde Ventas →
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* RESUMEN */}
      {tab === 'dashboard_old' && (
        <div style={{ padding: '14px 16px' }}>
          <h3 style={{ color: '#b8860b', marginTop: 0 }}>Resumen general</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Total vendido',  value: fmt(ventas.reduce((a,v)=>a+parseFloat(v.total||0),0)),  color: '#b8860b' },
              { label: 'Total cobrado',  value: fmt(ventas.reduce((a,v)=>a+parseFloat(v.pagado||0),0)), color: '#2e7d32' },
              { label: 'Total adeudado', value: fmt(ventas.reduce((a,v)=>a+parseFloat(v.saldo||0),0)),  color: '#e65100' },
              { label: 'Clientes',       value: clientes.length,                                         color: '#5fa8a0' },
              { label: 'Perfumes',       value: perfumes.length,                                         color: '#7b1fa2' },
              { label: 'Sin stock',      value: perfumes.filter(p=>p.stock===0).length,                  color: '#e53935' },
            ].map((s,i) => (
              <div key={i} style={{ ...S.card, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>
          <h4 style={{ color: '#b8860b' }}>Clientes con deuda</h4>
          {clientes.filter(c => parseFloat(c.deuda_total) > 0).map(c => (
            <div key={c.id} style={{ ...S.card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>{c.nombre}</span>
              <span style={S.tag('warn')}>Debe {fmt(c.deuda_total)}</span>
            </div>
          ))}
        </div>
      )}

      {/* EXPORTAR */}
      {tab === 'perfumes' && subTab === 'exportar' && (
        <div style={{ padding: '14px 16px' }}>
          <h3 style={{ color: '#b8860b', marginTop: 0 }}>Exportar datos</h3>
          <div style={S.card}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#5fa8a0' }}>Catalogo de perfumes</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button style={{ ...S.btn('gold'), flex: 1 }} onClick={exportarCatalogoPDF}>Exportar PDF</button>
              <button style={{ ...S.btn('teal'), flex: 1 }} onClick={exportarCatalogoExcel}>Exportar Excel</button>
            </div>
            <div style={{ fontSize: 12, color: '#aaa', marginTop: 8 }}>Incluye: nombre, marca, familia, precio, descuento y stock de los {perfumes.length} perfumes.</div>
          </div>
          <div style={S.card}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#5fa8a0' }}>Clientes y ventas</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button style={{ ...S.btn('gold'), flex: 1 }} onClick={exportarClientesPDF}>Exportar PDF</button>
              <button style={{ ...S.btn('teal'), flex: 1 }} onClick={exportarClientesExcel}>Exportar Excel</button>
            </div>
            <div style={{ fontSize: 12, color: '#aaa', marginTop: 8 }}>Incluye: historial de compras, pagos y saldos de los {clientes.length} clientes.</div>
          </div>
          <div style={S.card}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#5fa8a0' }}>Backup completo</div>
            <button style={{ ...S.btn('gold'), width: '100%', marginBottom: 10 }} onClick={exportarBackup}>Descargar backup JSON</button>
            <div style={{ fontSize: 12, color: '#aaa' }}>Descarga todos los datos en un archivo JSON. Guardalo como respaldo.</div>
          </div>
        </div>
      )}

      {/* RESUMEN / DASHBOARD */}
      {tab === 'dashboard_old' && (() => {
        const d = dashData || {};
        const fmtN = n => Math.round(Number(n) || 0).toLocaleString('es-AR');
        const fmt2 = n => (Number(n)||0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

        const statCard = (valor, label, sub, color) => (
          <div style={{ background:'#fff', borderRadius:10, padding:'14px 12px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', textAlign:'center', flex:1, minWidth:110 }}>
            <div style={{ fontSize:20, fontWeight:900, color: color||'#1a1a1a', lineHeight:1.1 }}>{valor}</div>
            <div style={{ fontSize:11, color:'#666', marginTop:5, fontWeight:600 }}>{label}</div>
            {sub && <div style={{ fontSize:10, color:'#aaa', marginTop:2 }}>{sub}</div>}
          </div>
        );

        return (
          <div style={{ padding:'14px 16px' }}>
            <div style={{ fontWeight:800, fontSize:16, marginBottom:14, color:'#b8860b', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              Resumen
              <button onClick={() => { setDashLoading(true); fetch('/api/dashboard',getAuth()).then(r=>r.json()).then(d=>{setDashData(d);setDashLoading(false);}).catch(()=>setDashLoading(false)); fetch('/api/visitas',getAuth()).then(r=>r.json()).then(v=>setVisitas(v)).catch(()=>{}); }}
                style={{ ...S.btn('teal'), fontSize:11, padding:'4px 10px' }}>{dashLoading ? '...' : '↺ Actualizar'}</button>
            </div>

            {dashLoading && !dashData && (
              <div style={{ textAlign:'center', color:'#aaa', padding:40 }}>Cargando...</div>
            )}

            {dashData && (
              <>
                {/* STAT CARDS */}
                <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
                  {statCard(`$${fmtN(d.ventasMes?.total_mes)}`, '💰 Ventas del mes', `${d.ventasMes?.count||0} pedidos`, '#b8860b')}
                  {statCard(d.stockBajo?.length||0, '⚠️ Stock bajo', '≤ 3 unidades', d.stockBajo?.length > 0 ? '#e53935' : '#4caf50')}
                  {statCard(d.pedidosCamino?.length||0, '📦 En camino', 'pedidos prov.', '#e65100')}
                  {statCard(`$${fmtN(d.ventasMes?.deuda_mes)}`, '🔴 Por cobrar', 'deuda activa', d.ventasMes?.deuda_mes > 0 ? '#e53935' : '#4caf50')}
                </div>

                {/* FILA PRINCIPAL */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>

                  {/* Stock bajo */}
                  <div style={{ background:'#fff', borderRadius:10, padding:'12px 14px', boxShadow:'0 2px 8px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontWeight:700, fontSize:13, marginBottom:8, color:'#555' }}>⚠️ Stock bajo</div>
                    {(d.stockBajo||[]).length === 0
                      ? <div style={{ fontSize:12, color:'#4caf50' }}>✓ Todo en orden</div>
                      : (d.stockBajo||[]).map(p => (
                        <div key={p.id} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid #f5f5f5', fontSize:12 }}>
                          <span style={{ color:'#333' }}>{p.nombre}</span>
                          <span style={{ fontWeight:700, color: p.stock===0?'#e53935':'#ff7043', background: p.stock===0?'#fce4ec':'#fff3e0', borderRadius:4, padding:'1px 6px', fontSize:11 }}>
                            {p.stock===0 ? 'Sin stock' : `${p.stock} u.`}
                          </span>
                        </div>
                      ))
                    }
                  </div>

                  {/* Pedidos en camino + Próximamente */}
                  <div style={{ background:'#fff', borderRadius:10, padding:'12px 14px', boxShadow:'0 2px 8px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontWeight:700, fontSize:13, marginBottom:8, color:'#555' }}>📦 Pedidos en camino</div>
                    {(d.pedidosCamino||[]).length === 0
                      ? <div style={{ fontSize:12, color:'#aaa' }}>Sin pedidos activos</div>
                      : (d.pedidosCamino||[]).map(p => (
                        <div key={p.id} style={{ padding:'6px 8px', background:'#fff8e1', borderRadius:6, marginBottom:5, fontSize:12 }}>
                          <div style={{ fontWeight:700, color:'#e65100' }}>{p.proveedor_nombre||'Sin nombre'}</div>
                          <div style={{ fontSize:11, color:'#888' }}>{p.fecha ? new Date(p.fecha).toLocaleDateString('es-AR') : ''} · ${fmtN(p.total_ars)}</div>
                        </div>
                      ))
                    }
                    {(d.proximamente||[]).length > 0 && (
                      <>
                        <div style={{ fontWeight:700, fontSize:12, margin:'10px 0 6px', color:'#b8860b' }}>✨ Próximamente</div>
                        {(d.proximamente||[]).map(p => (
                          <div key={p.id} style={{ fontSize:12, color:'#b8860b', padding:'2px 0' }}>• {p.nombre}</div>
                        ))}
                      </>
                    )}
                  </div>
                </div>

                {/* FILA INFERIOR */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>

                  {/* Top perfumes del mes */}
                  <div style={{ background:'#fff', borderRadius:10, padding:'12px 14px', boxShadow:'0 2px 8px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontWeight:700, fontSize:13, marginBottom:8, color:'#555' }}>🏆 Más vendidos del mes</div>
                    {(d.topPerfumes||[]).length === 0
                      ? <div style={{ fontSize:12, color:'#aaa' }}>Sin ventas este mes</div>
                      : (d.topPerfumes||[]).map((p,i) => (
                        <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0', borderBottom:'1px solid #f5f5f5', fontSize:12 }}>
                          <span style={{ color:'#333' }}>
                            <span style={{ color: i===0?'#f0c040':i===1?'#aaa':i===2?'#cd7f32':'#bbb', fontWeight:700, marginRight:5 }}>
                              {i===0?'🥇':i===1?'🥈':i===2?'🥉':`${i+1}.`}
                            </span>
                            {p.nombre_perfume}
                          </span>
                          <span style={{ color:'#b8860b', fontWeight:700 }}>{p.total_vendido} u.</span>
                        </div>
                      ))
                    }
                  </div>

                  {/* Últimas ventas */}
                  <div style={{ background:'#fff', borderRadius:10, padding:'12px 14px', boxShadow:'0 2px 8px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontWeight:700, fontSize:13, marginBottom:8, color:'#555' }}>🕐 Últimas ventas</div>
                    {(d.ultimasVentas||[]).length === 0
                      ? <div style={{ fontSize:12, color:'#aaa' }}>Sin ventas recientes</div>
                      : (d.ultimasVentas||[]).map(v => (
                        <div key={v.id} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid #f5f5f5', fontSize:12 }}>
                          <div>
                            <div style={{ color:'#333', fontWeight:600 }}>{v.cliente_nombre}</div>
                            <div style={{ fontSize:10, color:'#aaa' }}>{v.fecha ? new Date(v.fecha).toLocaleDateString('es-AR') : ''} · {v.forma_pago}</div>
                          </div>
                          <div style={{ textAlign:'right' }}>
                            <div style={{ fontWeight:700 }}>${fmtN(v.total)}</div>
                            {Number(v.pagado) < Number(v.total) && (
                              <div style={{ fontSize:10, color:'#e53935', fontWeight:600 }}>Debe ${fmtN(v.total - v.pagado)}</div>
                            )}
                          </div>
                        </div>
                      ))
                    }

                    {/* Deuda por cliente */}
                    {(d.deudaClientes||[]).length > 0 && (
                      <>
                        <div style={{ fontWeight:700, fontSize:12, margin:'10px 0 6px', color:'#e53935' }}>💳 Deuda por cliente</div>
                        {(d.deudaClientes||[]).map((c,i) => (
                          <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12, padding:'3px 0' }}>
                            <span style={{ color:'#555' }}>{c.nombre}</span>
                            <span style={{ color:'#e53935', fontWeight:700 }}>${fmtN(c.deuda)}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* PROVEEDORES */}
      {tab === 'proveedores' && (() => {
        const tc = Number(pedidoForm.tc) || Number(pedidoForm.moneda === 'USD' ? provConfig.usdt : provConfig.brl) || 1;
        const envioIndiv = Number(pedidoForm.envioIndiv) || 0;
        const envioPeso  = Number(pedidoForm.envioPeso)  || 0;
        const itemsValidos = pedidoForm.items.filter(i => i.nombre && Number(i.cantidad) > 0 && Number(i.precio) > 0);
        const totalUnidades = itemsValidos.reduce((s, i) => s + Number(i.cantidad), 0);
        const envioPesoXU = totalUnidades > 0 ? envioPeso / totalUnidades : 0;
        const fmt  = n => Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const fmtN = n => Math.round(Number(n)).toLocaleString('es-AR');

        // Calc por item (todos los rows, para mostrar en tabla)
        const itemCalcs = pedidoForm.items.map(i => {
          const qty    = Number(i.cantidad) || 0;
          const precio = Number(i.precio)   || 0;
          const convXU        = precio * tc;
          const costoSinFlete = convXU + envioIndiv;
          const costoFinal    = costoSinFlete + envioPesoXU;
          return { ...i, qty, precio, convXU, costoSinFlete, costoFinal };
        });

        const totalArs = itemCalcs.reduce((s, i) => s + i.costoFinal * i.qty, 0);

        const guardarPedido = async (estado) => {
          if (!pedidoForm.provNombre || itemsValidos.length === 0) return;
          const rp = await fetch('/api/proveedores', {
            method: 'POST',
            headers: { ...getAuth().headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ nombre: pedidoForm.provNombre }),
          });
          const prov = await rp.json();
          const totalDivisa = itemsValidos.reduce((s,i) => s + Number(i.cantidad)*Number(i.precio), 0);
          const pedBody = {
            proveedor_id: prov.id, moneda: pedidoForm.moneda, tipo_cambio: tc,
            envio1_monto: envioIndiv, envio2_monto: envioPeso,
            items: itemsValidos, total_divisa: totalDivisa, total_ars: totalArs,
            costo_por_unidad_ars: totalUnidades > 0 ? totalArs / totalUnidades : 0,
            notas: pedidoForm.notas, estado,
          };
          if (editandoPedidoId) {
            await fetch('/api/proveedores/pedidos/' + editandoPedidoId, {
              method: 'PUT',
              headers: { ...getAuth().headers, 'Content-Type': 'application/json' },
              body: JSON.stringify(pedBody),
            });
            fetch('/api/proveedores/pedidos', getAuth()).then(r => r.json()).then(d => { if (Array.isArray(d)) setPedidosProv(d); });
            fetch('/api/proveedores', getAuth()).then(r => r.json()).then(d => { if (Array.isArray(d)) setProveedores(d); });
            setEditandoPedidoId(null);
            setPedidoGuardado(Date.now());
            setTimeout(() => setPedidoGuardado(null), 3000);
            setPedidoForm({ provNombre: '', moneda: 'USD', tc: '', envioIndiv: '', envioPeso: '', items: [{ nombre: '', cantidad: '1', precio: '' }], notas: '' });
            return;
          }
          await fetch('/api/proveedores/pedidos', {
            method: 'POST',
            headers: { ...getAuth().headers, 'Content-Type': 'application/json' },
            body: JSON.stringify(pedBody),
          });
          // Si es pedido confirmado, marcar perfumes como próximamente
          const pedR2 = await fetch('/api/proveedores/pedidos', getAuth()).then(r => r.json());
          if (Array.isArray(pedR2)) setPedidosProv(pedR2);
          if (estado === 'pedido' && pedR2.length > 0) {
            // Encontrar el pedido recién creado (el primero, más reciente)
            const nuevo = pedR2[0];
            if (nuevo) {
              await fetch('/api/proveedores/pedidos/' + nuevo.id + '/marcar-pedido', {
                method: 'PATCH', headers: { ...getAuth().headers, 'Content-Type': 'application/json' }, body: '{}',
              });
            }
          }
          fetch('/api/proveedores', getAuth()).then(r => r.json()).then(d => { if (Array.isArray(d)) setProveedores(d); });
          setPedidoGuardado(Date.now());
          setTimeout(() => setPedidoGuardado(null), 3000);
          setPedidoForm({ provNombre: '', moneda: 'USD', tc: '', envioIndiv: '', envioPeso: '', items: [{ nombre: '', cantidad: '1', precio: '' }], notas: '' });
        };

        const cambiarEstado = async (id, estado) => {
          const endpoint = estado === 'pedido'
            ? '/api/proveedores/pedidos/' + id + '/marcar-pedido'
            : '/api/proveedores/pedidos/' + id + '/estado';
          await fetch(endpoint, {
            method: 'PATCH',
            headers: { ...getAuth().headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ estado }),
          });
          fetch('/api/proveedores/pedidos', getAuth()).then(r => r.json()).then(d => { if (Array.isArray(d)) setPedidosProv(d); });
        };

        const exportarPedidoPDF = (p) => {
          const doc = new jsPDF();
          const its = typeof p.items === 'string' ? JSON.parse(p.items) : (p.items || []);
          const tcP   = Number(p.tipo_cambio)  || 1;
          const envIP = Number(p.envio1_monto) || 0;
          const envPP = Number(p.envio2_monto) || 0;
          const totalUP  = its.reduce((s, i) => s + Number(i.cantidad), 0);
          const pesoXUP  = totalUP > 0 ? envPP / totalUP : 0;
          const calcs = its.map(i => ({
            nombre:   i.nombre,
            cant:     Number(i.cantidad),
            precio:   Number(i.precio),
            convXU:   Number(i.precio) * tcP,
            costoSF:  Number(i.precio) * tcP + envIP,
            costoF:   Number(i.precio) * tcP + envIP + pesoXUP,
          }));
          const fN = n => Math.round(Number(n)).toLocaleString('es-AR');
          const f2 = n => Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

          // ── Encabezado ──────────────────────────────────────
          doc.setFontSize(16); doc.setTextColor(184, 134, 11);
          doc.text('Pedido a Proveedor', 14, 18);
          doc.setFontSize(12); doc.setTextColor(50);
          doc.text(p.proveedor_nombre || 'Sin proveedor', 14, 26);
          doc.setFontSize(9); doc.setTextColor(120);
          const fecha = p.fecha
            ? new Date(p.fecha).toLocaleDateString('es-AR')
            : new Date().toLocaleDateString('es-AR');
          doc.text(`Fecha: ${fecha}   Moneda: ${p.moneda}   TC: $${fN(p.tipo_cambio)}`, 14, 33);
          let startY = 40;
          if (envIP > 0) {
            doc.text(`Envio individual: $${fN(envIP)}/u`, 14, 38);
            startY = 44;
          }

          // ── Tabla de items ───────────────────────────────────
          const head = [['Perfume', 'Cant.', `Precio ${p.moneda}`, 'Conv. $', 'Envio/u', 'T. s/flete', 'Costo final/u']];
          const body = calcs.map(c => [
            c.nombre,
            c.cant,
            f2(c.precio),
            '$' + fN(c.convXU),
            envIP > 0 ? '$' + fN(envIP) : '-',
            '$' + fN(c.costoSF),
            '$' + f2(c.costoF),
          ]);
          body.push([
            'TOTAL', totalUP, '',
            '$' + fN(calcs.reduce((s,c) => s + c.convXU * c.cant, 0)),
            envIP > 0 ? '$' + fN(envIP * totalUP) : '-',
            '$' + fN(calcs.reduce((s,c) => s + c.costoSF * c.cant, 0)),
            '',
          ]);
          autoTable(doc, {
            head,
            body,
            startY,
            styles:      { fontSize: 9, cellPadding: 3 },
            headStyles:  { fillColor: [184, 134, 11], textColor: 255, fontStyle: 'bold' },
            columnStyles:{ 0: { cellWidth: 52 }, 1: { halign: 'center', cellWidth: 14 } },
            didParseCell: data => {
              if (data.row.index === body.length - 1) {
                data.cell.styles.fontStyle   = 'bold';
                data.cell.styles.fillColor   = [245, 245, 245];
                data.cell.styles.textColor   = [50, 50, 50];
              }
            },
          });

          let y = doc.lastAutoTable.finalY + 8;

          // ── Envio por peso ───────────────────────────────────
          if (envPP > 0) {
            doc.setFontSize(9); doc.setTextColor(80);
            doc.text(
              `Envio por peso: $${fN(envPP)} / ${totalUP} unidades = $${f2(pesoXUP)}/u`,
              14, y
            );
            y += 8;
          }

          // ── Caja resumen ─────────────────────────────────────
          const costoProm = Number(p.costo_por_unidad_ars) || (totalUP > 0 ? Number(p.total_ars) / totalUP : 0);
          doc.setFillColor(30, 30, 30);
          doc.roundedRect(14, y, 182, 20, 3, 3, 'F');
          doc.setFontSize(9); doc.setTextColor(180, 180, 180);
          doc.text('Costo promedio por unidad', 20, y + 7);
          doc.setFontSize(14); doc.setTextColor(79, 195, 247); doc.setFont('helvetica', 'bold');
          doc.text('$' + f2(costoProm), 20, y + 16);
          doc.setFontSize(10); doc.setTextColor(210, 210, 210); doc.setFont('helvetica', 'normal');
          doc.text(
            `Total: $${fN(p.total_ars)}   Unidades: ${totalUP}`,
            105, y + 12
          );

          // ── Guardar ──────────────────────────────────────────
          const slug = (p.proveedor_nombre || 'pedido').replace(/\s+/g, '-').toLowerCase();
          doc.save(`pedido-${slug}-${fecha.replace(/\//g, '-')}.pdf`);
        };

        const exportarFormPDF = () => {
          if (itemsValidos.length === 0) return;
          exportarPedidoPDF({
            proveedor_nombre:     pedidoForm.provNombre || 'Sin proveedor',
            moneda:               pedidoForm.moneda,
            tipo_cambio:          tc,
            envio1_monto:         envioIndiv,
            envio2_monto:         envioPeso,
            items:                itemsValidos,
            total_ars:            totalArs,
            costo_por_unidad_ars: totalUnidades > 0 ? totalArs / totalUnidades : 0,
            fecha:                new Date().toISOString(),
          });
        };

        return (
          <div style={{ padding: '14px 16px' }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 12, color: '#b8860b' }}>Proveedores</div>

            {/* Config tasas */}
            <div style={{ background: '#f8f8f8', borderRadius: 8, padding: '12px 14px', marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>USDT → ARS</div>
                <input type="number" placeholder="ej: 1488" value={provConfig.usdt || ''}
                  onChange={e => setProvConfig(c => ({...c, usdt: e.target.value}))}
                  style={{ width: 100, padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>BRL → ARS</div>
                <input type="number" placeholder="ej: 260" value={provConfig.brl || ''}
                  onChange={e => setProvConfig(c => ({...c, brl: e.target.value}))}
                  style={{ width: 100, padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }} />
              </div>
              <button style={{ ...S.btn('gold'), padding: '7px 14px', fontSize: 12 }}
                onClick={() => localStorage.setItem('prov_config', JSON.stringify(provConfig))}>
                Guardar tasas
              </button>
            </div>

            {/* Formulario nuevo presupuesto */}
            <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#333', display:'flex', alignItems:'center', gap:10 }}>
                {editandoPedidoId ? '✏️ Editando presupuesto' : 'Nuevo presupuesto'}
                {editandoPedidoId && (
                  <button onClick={() => { setEditandoPedidoId(null); setPedidoForm({provNombre:'',moneda:'USD',tc:'',envioIndiv:'',envioPeso:'',items:[{nombre:'',cantidad:'1',precio:''}],notas:''}); }}
                    style={{ ...S.btn(''), fontSize:11, padding:'3px 8px' }}>✕ Cancelar edición</button>
                )}
              </div>

              {/* Cabecera */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                <div style={{ flex: 2, minWidth: 140 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Proveedor</div>
                  <input list="prov-list" value={pedidoForm.provNombre}
                    onChange={e => setPedidoForm(f => ({...f, provNombre: e.target.value}))}
                    placeholder="Nombre del proveedor"
                    style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                  <datalist id="prov-list">{proveedores.map(p => <option key={p.id} value={p.nombre} />)}</datalist>
                </div>
                <div style={{ minWidth: 100 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Moneda</div>
                  <select value={pedidoForm.moneda}
                    onChange={e => setPedidoForm(f => ({...f, moneda: e.target.value, tc: ''}))}
                    style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}>
                    <option value="USD">USD / USDT</option>
                    <option value="BRL">BRL / PIX</option>
                  </select>
                </div>
                <div style={{ minWidth: 110 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Tipo de cambio</div>
                  <input type="number" value={pedidoForm.tc}
                    placeholder={pedidoForm.moneda === 'USD' ? (provConfig.usdt || 'USDT→$') : (provConfig.brl || 'BRL→$')}
                    onChange={e => setPedidoForm(f => ({...f, tc: e.target.value}))}
                    style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div style={{ minWidth: 130 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Envío indiv. ($ ARS/u)</div>
                  <input type="number" step="1" value={pedidoForm.envioIndiv}
                    placeholder="ARS por unidad"
                    onChange={e => setPedidoForm(f => ({...f, envioIndiv: e.target.value}))}
                    style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>

              {/* Tabla de perfumes */}
              <div style={{ overflowX: 'auto', marginBottom: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left',   color: '#555', fontWeight: 600 }}>Perfume</th>
                      <th style={{ padding: '6px 6px', textAlign: 'center', color: '#555', fontWeight: 600, width: 56 }}>Cant.</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right',  color: '#555', fontWeight: 600, width: 86 }}>Precio {pedidoForm.moneda}</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right',  color: '#555', fontWeight: 600, width: 86 }}>Conv. $</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right',  color: '#888', fontWeight: 600, width: 76 }}>Envío/u</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right',  color: '#555', fontWeight: 600, width: 90 }}>Total s/flete</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right',  color: '#b8860b', fontWeight: 700, width: 100 }}>Costo final/u</th>
                      <th style={{ width: 26 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedidoForm.items.map((item, idx) => {
                      const c = itemCalcs[idx];
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '4px 6px' }}>
                            <input value={item.nombre}
                              onChange={e => setPedidoForm(f => { const it=[...f.items]; it[idx]={...it[idx],nombre:e.target.value}; return {...f,items:it}; })}
                              placeholder="Nombre del perfume"
                              style={{ width: '100%', padding: '4px 6px', borderRadius: 4, border: '1px solid #ddd', fontSize: 12 }} />
                          </td>
                          <td style={{ padding: '4px 3px' }}>
                            <input type="number" min="1" value={item.cantidad}
                              onChange={e => setPedidoForm(f => { const it=[...f.items]; it[idx]={...it[idx],cantidad:e.target.value}; return {...f,items:it}; })}
                              style={{ width: '100%', padding: '4px 3px', borderRadius: 4, border: '1px solid #ddd', fontSize: 12, textAlign: 'center' }} />
                          </td>
                          <td style={{ padding: '4px 3px' }}>
                            <input type="number" step="0.01" value={item.precio}
                              onChange={e => setPedidoForm(f => { const it=[...f.items]; it[idx]={...it[idx],precio:e.target.value}; return {...f,items:it}; })}
                              placeholder="0.00"
                              style={{ width: '100%', padding: '4px 6px', borderRadius: 4, border: '1px solid #ddd', fontSize: 12, textAlign: 'right' }} />
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', color: '#444' }}>
                            {c.precio > 0 ? fmtN(c.convXU) : <span style={{color:'#ccc'}}>—</span>}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', color: '#888' }}>
                            {envioIndiv > 0 ? fmtN(envioIndiv) : <span style={{color:'#ccc'}}>—</span>}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', color: '#444' }}>
                            {c.precio > 0 ? fmtN(c.costoSinFlete) : <span style={{color:'#ccc'}}>—</span>}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700, color: '#b8860b' }}>
                            {c.precio > 0 && totalUnidades > 0 ? fmt(c.costoFinal) : <span style={{color:'#ccc'}}>—</span>}
                          </td>
                          <td style={{ padding: '4px 2px' }}>
                            <button onClick={() => setPedidoForm(f => { const it=f.items.filter((_,i)=>i!==idx); return {...f,items:it.length?it:[{nombre:'',cantidad:'1',precio:''}]}; })}
                              style={{ background: 'none', border: 'none', color: '#e53935', cursor: 'pointer', fontSize: 16, padding: 0 }}>×</button>
                          </td>
                        </tr>
                      );
                    })}
                    {/* Fila total */}
                    {itemsValidos.length > 0 && (
                      <tr style={{ background: '#f9f9f9', fontWeight: 700, borderTop: '2px solid #e0e0e0' }}>
                        <td style={{ padding: '6px 8px', fontSize: 12, color: '#555' }}>Total</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 12 }}>{totalUnidades}</td>
                        <td></td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 12 }}>{fmtN(itemCalcs.filter(c=>c.precio>0).reduce((s,c)=>s+c.convXU*c.qty,0))}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 12 }}>{envioIndiv > 0 ? fmtN(envioIndiv * totalUnidades) : ''}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 12 }}>{fmtN(itemCalcs.filter(c=>c.precio>0).reduce((s,c)=>s+c.costoSinFlete*c.qty,0))}</td>
                        <td></td>
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <button onClick={() => setPedidoForm(f => ({...f, items:[...f.items,{nombre:'',cantidad:'1',precio:''}]}))}
                style={{ ...S.btn('teal'), fontSize: 12, padding: '5px 12px', marginBottom: 14 }}>+ Agregar perfume</button>

              {/* Envío por peso */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 14, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
                    Envío por peso — total $ ARS <span style={{color:'#bbb'}}>(completar con factura del flete)</span>
                  </div>
                  <input type="number" step="1" value={pedidoForm.envioPeso}
                    placeholder="0  (podés completarlo después)"
                    onChange={e => setPedidoForm(f => ({...f, envioPeso: e.target.value}))}
                    style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                {totalUnidades > 0 && envioPeso > 0 && (
                  <div style={{ fontSize: 12, color: '#666', paddingBottom: 9 }}>
                    = <strong style={{color:'#333'}}>${fmt(envioPesoXU)}</strong>/u ({totalUnidades} u.)
                  </div>
                )}
              </div>

              {/* Notas */}
              <input value={pedidoForm.notas}
                onChange={e => setPedidoForm(f => ({...f, notas: e.target.value}))}
                placeholder="Notas opcionales"
                style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, marginBottom: 14, boxSizing: 'border-box' }} />

              {/* Resumen oscuro */}
              {itemsValidos.length > 0 && (
                <div style={{ background: 'linear-gradient(135deg,#1a1a1a,#2d2d2d)', borderRadius: 10, padding: '14px 16px', marginBottom: 14, color: '#fff' }}>
                  <div style={{ fontSize: 11, color: '#aaa', marginBottom: 10, letterSpacing: 1, textTransform: 'uppercase' }}>Resumen</div>
                  {itemCalcs.filter(c => c.precio > 0).map((c, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12 }}>
                      <span style={{ color: '#ccc' }}>{c.nombre || '—'} ×{c.qty}</span>
                      <span>
                        {fmt(c.precio)} {pedidoForm.moneda}/u
                        <span style={{color:'#aaa'}}> → conv ${fmtN(c.convXU)} + env ${fmtN(envioIndiv)}{envioPeso>0?' + flete $'+fmtN(envioPesoXU):''} = </span>
                        <strong style={{color:'#f0c040'}}>${fmt(c.costoFinal)}/u</strong>
                      </span>
                    </div>
                  ))}
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', marginTop: 8, paddingTop: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: '#aaa' }}>Total {totalUnidades} unidades</span>
                      <span style={{ color: '#fff' }}>${fmtN(totalArs)}</span>
                    </div>
                    {envioPeso > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                        <span style={{ color: '#aaa' }}>Envío por peso</span>
                        <span style={{ color: '#fff' }}>${fmtN(envioPeso)} ÷ {totalUnidades}u = ${fmt(envioPesoXU)}/u</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, background: 'rgba(255,255,255,0.08)', borderRadius: 6, padding: '8px 10px', marginTop: 6 }}>
                      <span style={{ color: '#4fc3f7', fontWeight: 700 }}>Costo promedio/u</span>
                      <span style={{ color: '#4fc3f7', fontWeight: 900, fontSize: 16 }}>${fmt(totalUnidades > 0 ? totalArs/totalUnidades : 0)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Botones guardar */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button disabled={!pedidoForm.provNombre || itemsValidos.length === 0}
                  onClick={() => guardarPedido('borrador')}
                  style={{ flex: 1, ...S.btn('teal'), opacity: (!pedidoForm.provNombre||itemsValidos.length===0)?0.5:1, fontSize: 13 }}>
                  {pedidoGuardado ? '✓ Guardado!' : editandoPedidoId ? '💾 Guardar cambios' : '💾 Guardar borrador'}
                </button>
                <button disabled={!pedidoForm.provNombre || itemsValidos.length === 0}
                  onClick={() => guardarPedido('pedido')}
                  style={{ flex: 1, ...S.btn('gold'), opacity: (!pedidoForm.provNombre||itemsValidos.length===0)?0.5:1, fontSize: 13 }}>
                  📦 Confirmar pedido
                </button>
              </div>
              {itemsValidos.length > 0 && (
                <button onClick={exportarFormPDF}
                  style={{ ...S.btn(''), width:'100%', marginTop:8, fontSize:12, border:'1px solid #ccc', background:'#fafafa' }}>
                  📄 Exportar PDF del presupuesto
                </button>
              )}
            </div>

            {/* Historial */}
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: '#555' }}>Historial</div>
            {pedidosProv.length === 0 && <div style={{ color: '#aaa', fontSize: 13 }}>No hay presupuestos guardados.</div>}
            {pedidosProv.map(p => {
              const badge = p.estado === 'recibido' ? { bg:'#e8f5e9', color:'#2e7d32', text:'✓ Recibido' }
                : p.estado === 'pedido' ? { bg:'#fff3e0', color:'#e65100', text:'📦 En camino' }
                : { bg:'#f5f5f5', color:'#888', text:'Borrador' };
              const its = p.items ? (typeof p.items==='string' ? JSON.parse(p.items) : p.items) : [];
              const isOpen = expandedPedido === p.id;

              // Recalc detalle
              const tcP = Number(p.tipo_cambio) || 1;
              const envIP = Number(p.envio1_monto) || 0;
              const envPP = Number(p.envio2_monto) || 0;
              const totalUP = its.reduce((s,i) => s + Number(i.cantidad), 0);
              const pesoXUP = totalUP > 0 ? envPP / totalUP : 0;
              const itemsCalcP = its.map(i => ({
                ...i,
                qty: Number(i.cantidad),
                convXU: Number(i.precio) * tcP,
                costoSF: Number(i.precio) * tcP + envIP,
                costoF:  Number(i.precio) * tcP + envIP + pesoXUP,
              }));

              const clonar = () => {
                setPedidoForm({
                  provNombre: p.proveedor_nombre || '',
                  moneda: p.moneda || 'USD',
                  tc: String(p.tipo_cambio || ''),
                  envioIndiv: String(p.envio1_monto || ''),
                  envioPeso: String(p.envio2_monto || ''),
                  items: its.length > 0
                    ? its.map(i => ({ nombre: i.nombre||'', cantidad: String(i.cantidad||1), precio: String(i.precio||'') }))
                    : [{ nombre:'', cantidad:'1', precio:'' }],
                  notas: p.notas || '',
                });
                setExpandedPedido(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              };

              return (
                <div key={p.id} style={{ ...S.card, marginBottom: 8, overflow: 'hidden' }}>
                  {/* Cabecera clickeable */}
                  <div
                    onClick={() => setExpandedPedido(isOpen ? null : p.id)}
                    style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:6, cursor:'pointer', userSelect:'none' }}>
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <span style={{ fontSize:12, color:'#aaa', transition:'transform 0.2s', display:'inline-block', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                        <div style={{ fontWeight:700, fontSize:13 }}>{p.proveedor_nombre || 'Sin proveedor'}</div>
                      </div>
                      <div style={{ fontSize:11, color:'#888', marginLeft:18 }}>
                        {p.fecha ? new Date(p.fecha).toLocaleDateString('es-AR') : ''} · {p.moneda} · TC: {p.tipo_cambio}
                        {Number(p.envio1_monto)>0 && <span> · Env/u: ${fmtN(p.envio1_monto)}</span>}
                        {Number(p.envio2_monto)>0 && <span> · Flete: ${fmtN(p.envio2_monto)}</span>}
                      </div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ background:badge.bg, color:badge.color, borderRadius:4, padding:'2px 8px', fontSize:11, fontWeight:700 }}>
                        {badge.text}
                      </span>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontWeight:800, color:'#b8860b', fontSize:14 }}>${fmtN(p.total_ars)}</div>
                        <div style={{ fontSize:11, color:'#4fc3f7', fontWeight:700 }}>Prom: ${fmt(p.costo_por_unidad_ars)}/u</div>
                      </div>
                    </div>
                  </div>

                  {/* Vista colapsada: resumen rápido */}
                  {!isOpen && its.length > 0 && (
                    <div style={{ marginTop:5, fontSize:12, color:'#888', marginLeft:18 }}>
                      {its.map((it,i) => <span key={i} style={{ marginRight:10 }}>{it.nombre} ×{it.cantidad}</span>)}
                    </div>
                  )}

                  {/* Vista expandida: tabla completa */}
                  {isOpen && (
                    <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                          <thead>
                            <tr style={{ background:'#f5f5f5' }}>
                              <th style={{ padding:'5px 8px', textAlign:'left',   color:'#555', fontWeight:600 }}>Perfume</th>
                              <th style={{ padding:'5px 6px', textAlign:'center', color:'#555', fontWeight:600, width:46 }}>Cant.</th>
                              <th style={{ padding:'5px 8px', textAlign:'right',  color:'#555', fontWeight:600, width:72 }}>Precio {p.moneda}</th>
                              <th style={{ padding:'5px 8px', textAlign:'right',  color:'#555', fontWeight:600, width:78 }}>Conv. $</th>
                              {envIP > 0 && <th style={{ padding:'5px 8px', textAlign:'right', color:'#888', fontWeight:600, width:66 }}>Envío/u</th>}
                              <th style={{ padding:'5px 8px', textAlign:'right',  color:'#555', fontWeight:600, width:80 }}>T. s/flete</th>
                              <th style={{ padding:'5px 8px', textAlign:'right',  color:'#b8860b', fontWeight:700, width:92 }}>Costo final/u</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itemsCalcP.map((c, i) => (
                              <tr key={i} style={{ borderBottom:'1px solid #f5f5f5' }}>
                                <td style={{ padding:'5px 8px', color:'#333' }}>{c.nombre}</td>
                                <td style={{ padding:'5px 6px', textAlign:'center', color:'#555' }}>{c.qty}</td>
                                <td style={{ padding:'5px 8px', textAlign:'right', color:'#555' }}>{fmt(c.precio)}</td>
                                <td style={{ padding:'5px 8px', textAlign:'right', color:'#444' }}>{fmtN(c.convXU)}</td>
                                {envIP > 0 && <td style={{ padding:'5px 8px', textAlign:'right', color:'#888' }}>{fmtN(envIP)}</td>}
                                <td style={{ padding:'5px 8px', textAlign:'right', color:'#444' }}>{fmtN(c.costoSF)}</td>
                                <td style={{ padding:'5px 8px', textAlign:'right', color:'#b8860b', fontWeight:700 }}>{fmt(c.costoF)}</td>
                              </tr>
                            ))}
                            {/* Fila total */}
                            <tr style={{ background:'#f9f9f9', borderTop:'2px solid #e8e8e8', fontWeight:700 }}>
                              <td style={{ padding:'5px 8px', color:'#555', fontSize:11 }}>Total</td>
                              <td style={{ padding:'5px 6px', textAlign:'center', fontSize:11 }}>{totalUP}</td>
                              <td></td>
                              <td style={{ padding:'5px 8px', textAlign:'right', fontSize:11 }}>{fmtN(itemsCalcP.reduce((s,c)=>s+c.convXU*c.qty,0))}</td>
                              {envIP > 0 && <td style={{ padding:'5px 8px', textAlign:'right', fontSize:11 }}>{fmtN(envIP * totalUP)}</td>}
                              <td style={{ padding:'5px 8px', textAlign:'right', fontSize:11 }}>{fmtN(itemsCalcP.reduce((s,c)=>s+c.costoSF*c.qty,0))}</td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      {/* Envío por peso */}
                      {envPP > 0 && (
                        <div style={{ marginTop:8, fontSize:12, color:'#666', background:'#fafafa', borderRadius:6, padding:'6px 10px' }}>
                          Envío por peso: <strong>${fmtN(envPP)}</strong> ÷ {totalUP}u = <strong style={{color:'#333'}}>${fmt(pesoXUP)}/u</strong>
                        </div>
                      )}
                      {/* Resumen final */}
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:10, background:'linear-gradient(135deg,#1a1a1a,#2d2d2d)', borderRadius:8, padding:'10px 14px', color:'#fff' }}>
                        <span style={{ fontSize:12, color:'#aaa' }}>Costo promedio por unidad</span>
                        <span style={{ fontSize:17, fontWeight:900, color:'#4fc3f7' }}>${fmt(p.costo_por_unidad_ars)}</span>
                      </div>
                      {/* Acciones */}
                      <div style={{ display:'flex', gap:8, marginTop:10, flexWrap:'wrap' }}>
                        <button onClick={clonar}
                          style={{ ...S.btn('teal'), fontSize:11, padding:'5px 12px' }}>📋 Clonar</button>
                        <button onClick={() => {
                          setEditandoPedidoId(p.id);
                          setPedidoForm({
                            provNombre: p.proveedor_nombre || '',
                            moneda: p.moneda || 'USD',
                            tc: String(p.tipo_cambio || ''),
                            envioIndiv: String(p.envio1_monto || ''),
                            envioPeso: String(p.envio2_monto || ''),
                            items: its.length > 0
                              ? its.map(i => ({ nombre: i.nombre||'', cantidad: String(i.cantidad||1), precio: String(i.precio||'') }))
                              : [{ nombre:'', cantidad:'1', precio:'' }],
                            notas: p.notas || '',
                          });
                          setExpandedPedido(null);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} style={{ ...S.btn(''), fontSize:11, padding:'5px 12px', border:'1px solid #ccc' }}>✏️ Editar</button>
                        <button onClick={() => exportarPedidoPDF(p)}
                          style={{ ...S.btn(''), fontSize:11, padding:'5px 12px', border:'1px solid #ccc', background:'#fafafa' }}>📄 PDF</button>
                        <button onClick={() => setConfirmDelete({ id: p.id, desc: `${p.proveedor_nombre || 'Sin proveedor'} · $${fmtN(p.total_ars)}`, action: () => eliminarPedido(p.id) })}
                          style={{ background:'none', border:'1px solid #ffcdd2', borderRadius:6, color:'#e53935', cursor:'pointer', padding:'5px 12px', fontSize:11 }}>🗑 Eliminar</button>
                        {p.estado === 'borrador' && (
                          <button onClick={() => cambiarEstado(p.id,'pedido')}
                            style={{ ...S.btn('gold'), fontSize:11, padding:'5px 12px' }}>📦 Confirmar pedido</button>
                        )}
                        {p.estado === 'pedido' && (
                          <button onClick={() => cambiarEstado(p.id,'recibido')}
                            style={{ ...S.btn('teal'), fontSize:11, padding:'5px 12px' }}>✓ Marcar recibido</button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Acciones en vista colapsada */}
                  {!isOpen && (
                    <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap', alignItems:'center' }}>
                      {p.estado === 'borrador' && (
                        <button onClick={e => { e.stopPropagation(); cambiarEstado(p.id,'pedido'); }}
                          style={{ ...S.btn('gold'), fontSize:11, padding:'4px 10px' }}>📦 Confirmar pedido</button>
                      )}
                      {p.estado === 'pedido' && (
                        <button onClick={e => { e.stopPropagation(); cambiarEstado(p.id,'recibido'); }}
                          style={{ ...S.btn('teal'), fontSize:11, padding:'4px 10px' }}>✓ Marcar recibido</button>
                      )}
                      <button onClick={e => { e.stopPropagation(); setConfirmDelete({ id: p.id, desc: `${p.proveedor_nombre || 'Sin proveedor'} · $${fmtN(p.total_ars)}`, action: () => eliminarPedido(p.id) }); }}
                        style={{ background:'none', border:'1px solid #ffcdd2', borderRadius:6, color:'#e53935', cursor:'pointer', padding:'4px 10px', fontSize:11, marginLeft:'auto' }}>🗑</button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Comparar presupuestos */}
            {pedidosProv.length >= 2 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#555' }}>Comparar presupuestos</div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Presupuesto A</div>
                    <select value={compareA} onChange={e => setCompareA(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}>
                      <option value="">— Elegir —</option>
                      {pedidosProv.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.proveedor_nombre} · {p.fecha ? new Date(p.fecha).toLocaleDateString('es-AR') : ''} · TC:{p.tipo_cambio}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Presupuesto B</div>
                    <select value={compareB} onChange={e => setCompareB(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}>
                      <option value="">— Elegir —</option>
                      {pedidosProv.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.proveedor_nombre} · {p.fecha ? new Date(p.fecha).toLocaleDateString('es-AR') : ''} · TC:{p.tipo_cambio}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {compareA && compareB && compareA !== compareB && (() => {
                  const pA = pedidosProv.find(p => String(p.id) === String(compareA));
                  const pB = pedidosProv.find(p => String(p.id) === String(compareB));
                  if (!pA || !pB) return null;
                  const calcIt = p => {
                    const its = typeof p.items==='string' ? JSON.parse(p.items) : (p.items||[]);
                    const totalU = its.reduce((s,i)=>s+Number(i.cantidad),0);
                    const pesoXU = totalU>0 ? Number(p.envio2_monto)/totalU : 0;
                    return its.map(i => ({
                      nombre: i.nombre,
                      cantidad: Number(i.cantidad),
                      costoFinal: Number(i.precio)*Number(p.tipo_cambio) + Number(p.envio1_monto) + pesoXU,
                    }));
                  };
                  const itA = calcIt(pA);
                  const itB = calcIt(pB);
                  const nombres = [...new Set([...itA.map(i=>i.nombre), ...itB.map(i=>i.nombre)])];
                  const avgOf = its => its.length>0 ? its.reduce((s,i)=>s+i.costoFinal*i.cantidad,0)/its.reduce((s,i)=>s+i.cantidad,0) : 0;
                  const avgA = avgOf(itA), avgB = avgOf(itB);
                  return (
                    <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e0e0e0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#f5f5f5' }}>
                            <th style={{ padding: '8px 10px', textAlign: 'left', color: '#555', fontWeight: 600 }}>Perfume</th>
                            <th style={{ padding: '8px 10px', textAlign: 'right', color: '#1565c0', fontWeight: 700, minWidth: 140 }}>
                              {pA.proveedor_nombre}<br/><span style={{fontWeight:400,color:'#888',fontSize:10}}>TC: {pA.tipo_cambio}</span>
                            </th>
                            <th style={{ padding: '8px 10px', textAlign: 'right', color: '#6a1b9a', fontWeight: 700, minWidth: 140 }}>
                              {pB.proveedor_nombre}<br/><span style={{fontWeight:400,color:'#888',fontSize:10}}>TC: {pB.tipo_cambio}</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {nombres.map((nombre, i) => {
                            const iA = itA.find(x=>x.nombre===nombre);
                            const iB = itB.find(x=>x.nombre===nombre);
                            const aWins = iA && iB && iA.costoFinal <= iB.costoFinal;
                            const bWins = iA && iB && iB.costoFinal < iA.costoFinal;
                            return (
                              <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '7px 10px', color: '#333' }}>{nombre}</td>
                                <td style={{ padding: '7px 10px', textAlign: 'right',
                                  background: aWins ? '#e8f5e9' : 'transparent',
                                  color: aWins ? '#2e7d32' : (iA ? '#333' : '#ccc'), fontWeight: aWins ? 700 : 400 }}>
                                  {iA ? `$${fmt(iA.costoFinal)}/u${aWins?' ✓':''}` : '—'}
                                </td>
                                <td style={{ padding: '7px 10px', textAlign: 'right',
                                  background: bWins ? '#e8f5e9' : 'transparent',
                                  color: bWins ? '#2e7d32' : (iB ? '#333' : '#ccc'), fontWeight: bWins ? 700 : 400 }}>
                                  {iB ? `$${fmt(iB.costoFinal)}/u${bWins?' ✓':''}` : '—'}
                                </td>
                              </tr>
                            );
                          })}
                          <tr style={{ background: '#f0f0f0', borderTop: '2px solid #ddd', fontWeight: 700 }}>
                            <td style={{ padding: '8px 10px', color: '#555' }}>Promedio/u</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 13,
                              background: avgA<=avgB ? '#e8f5e9' : 'transparent',
                              color: avgA<=avgB ? '#2e7d32' : '#1565c0' }}>
                              ${fmt(avgA)}{avgA<=avgB ? ' ✓ ELEGIR' : ''}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 13,
                              background: avgB<avgA ? '#e8f5e9' : 'transparent',
                              color: avgB<avgA ? '#2e7d32' : '#6a1b9a' }}>
                              ${fmt(avgB)}{avgB<avgA ? ' ✓ ELEGIR' : ''}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        );
      })()}

      {/* BANNER */}
      {tab === 'banner' && (() => {
        const EVENTOS = [
          { key: 'padre',        label: 'Día del Padre' },
          { key: 'madre',        label: 'Día de la Madre' },
          { key: 'navidad',      label: 'Navidad' },
          { key: 'halloween',    label: 'Halloween' },
          { key: 'graduaciones', label: 'Graduaciones' },
          { key: 'valentin',     label: 'San Valentín' },
          { key: 'mujer',        label: 'Día de la Mujer' },
          { key: 'nino',         label: 'Día del Niño' },
          { key: 'anonuevo',     label: 'Año Nuevo' },
        ];
        return (
          <div style={{ padding: '20px 24px', maxWidth: 900, margin: '0 auto' }}>

            <div style={{ background: '#f9f8f6', border: '1px solid #ececec', borderRadius: 2, padding: '12px 16px', marginBottom: 20, fontSize: 11, color: '#888', lineHeight: 1.8 }}>
              <strong style={{ color: '#111', display: 'block', marginBottom: 4 }}>Reglas de archivos</strong>
              📹 <strong>Videos:</strong> MP4 · máx 150MB · loop 10–30 seg recomendado · 720p o 1080p<br/>
              🖼️ <strong>Imágenes:</strong> JPG o PNG · máx 5MB · relación 16:9 (horizontal) o 9:16 (vertical)<br/>
              📁 <strong>Orden:</strong> el primer archivo cargado se muestra primero, luego rotan en orden
            </div>

            {EVENTOS.map(ev => (
              <EventoBannerPanel key={ev.key} ev={ev} getAuth={getAuth} showToast={showToast} API={API} />
            ))}

            <div style={{ marginTop: 16, fontSize: 11, color: '#bbb', textAlign: 'center' }}>
              Para previsualizar un evento antes de su fecha: abrí el catálogo con <code>?preview=padre</code>
            </div>
          </div>
        );
      })()}


      {/* PRESUPUESTO */}
      {tab === 'presupuesto' && (() => {
        const fmtP = n => '$' + Math.round(n).toLocaleString('es-AR');
        const tipoLabel = t => t === 'full' ? 'Completo' : t === '5ml' ? 'Decant 5ml' : 'Decant 10ml';
        const getBase = (p, t) => t === '5ml' ? (parseFloat(p.precio_decant_5ml)||0) : t === '10ml' ? (parseFloat(p.precio_decant_10ml)||0) : (parseFloat(p.precio)||0);
        let sub = 0;
        presupCart.forEach(item => { sub += Math.round(item.precio * item.qty * (1 - item.desc / 100)); });
        const gDiscAmt = Math.round(sub * presupGDesc / 100);
        const envioAmt = presupEnvioActivo ? (parseFloat(presupEnvioMonto) || 0) : 0;
        const total = sub - gDiscAmt + envioAmt;
        const filtrados = presupSearch.trim()
          ? perfumes.filter(p =>
              p.nombre.toLowerCase().includes(presupSearch.toLowerCase()) ||
              p.marca.toLowerCase().includes(presupSearch.toLowerCase()))
          : [];
        const qtyUp = (k, d) => setPresupCart(prev => prev.map(i => i.key === k ? {...i, qty: Math.max(1, i.qty + d)} : i));
        const setItemDesc = (k, d) => setPresupCart(prev => prev.map(i => i.key === k ? {...i, desc: d} : i));
        const setItemPrecio = (k, v) => setPresupCart(prev => prev.map(i => i.key === k ? {...i, precio: parseFloat(v)||0} : i));
        const setItemTipo = (k, t, p) => setPresupCart(prev => prev.map(i => i.key === k ? {...i, tipo: t, precio: getBase(p, t)} : i));
        const addItem = (p, t) => {
          const base = getBase(p, t);
          setPresupCart(prev => {
            const ex = prev.find(i => i.prod.id === p.id && i.tipo === t);
            if (ex) return prev.map(i => i.key === ex.key ? {...i, qty: i.qty + 1} : i);
            return [...prev, { key: Date.now() + Math.random(), prod: p, tipo: t, qty: 1, precio: base, desc: 0 }];
          });
          setPresupSearch('');
        };
        const removeItem = k => setPresupCart(prev => prev.filter(i => i.key !== k));
        const buildPayload = () => ({
          cliente: presupCliente,
          items: presupCart.map(i => ({ perfume_id: i.prod.id, nombre: i.prod.nombre, tipo: i.tipo, qty: i.qty, precio: i.precio, desc: i.desc })),
          total, descuento_global: presupGDesc, envio_activo: presupEnvioActivo, envio_monto: parseFloat(presupEnvioMonto) || 0
        });
        const loadPresupList = () => {
          axios.get(`${API}/presupuestos`, getAuth()).then(r => setPresupList(r.data)).catch(() => {});
        };
        const handleGuardar = async () => {
          try {
            const r = await axios.post(`${API}/presupuestos`, buildPayload(), getAuth());
            setPresupSavedId(r.data.id);
            setPresupList(prev => [r.data, ...prev.filter(p => p.id !== r.data.id)]);
            showToast('Presupuesto guardado #' + r.data.id);
          } catch(e) { showToast('Error al guardar'); }
        };
        const handleConfirmar = async () => {
          try {
            let sid = presupSavedId;
            if (!sid) {
              const rs = await axios.post(`${API}/presupuestos`, buildPayload(), getAuth());
              sid = rs.data.id; setPresupSavedId(sid);
              setPresupList(prev => [rs.data, ...prev]);
            }
            await axios.post(`${API}/presupuestos/${sid}/confirmar`, { forma_pago: presupFormaPago, pagado: parseFloat(presupPagado) || 0 }, getAuth());
            showToast('Venta creada');
            setPresupCart([]); setPresupCliente(''); setPresupGDesc(0);
            setPresupEnvioActivo(false); setPresupEnvioMonto(0);
            setPresupConfirmando(false); setPresupSavedId(null);
            setPresupList(prev => prev.map(p => p.id === sid ? {...p, estado: 'confirmado'} : p));
            setTab('ventas');
          } catch(e) { showToast('Error al confirmar: ' + (e.response?.data?.error || e.message)); }
        };
        const handleCargarPresup = (p) => {
          const its = Array.isArray(p.items) ? p.items : JSON.parse(p.items || '[]');
          const cart = its.map(i => {
            const prod = perfumes.find(pf => pf.id === i.perfume_id) || { id: i.perfume_id, nombre: i.nombre, marca: '', imagen_url: null, notas: '', precio: i.precio, precio_decant_5ml: 0, precio_decant_10ml: 0 };
            return { key: Date.now() + Math.random(), prod, tipo: i.tipo || 'full', qty: i.qty || 1, precio: i.precio, desc: i.desc || 0 };
          });
          setPresupCart(cart); setPresupCliente(p.cliente || '');
          setPresupGDesc(p.descuento_global || 0);
          setPresupEnvioActivo(p.envio_activo || false); setPresupEnvioMonto(p.envio_monto || 0);
          setPresupSavedId(p.id); showToast('Presupuesto #' + p.id + ' cargado');
        };
        const handleBorrarPresup = async (id) => {
          try {
            await axios.delete(`${API}/presupuestos/${id}`, getAuth());
            setPresupList(prev => prev.filter(p => p.id !== id));
            if (presupSavedId === id) setPresupSavedId(null);
          } catch(e) { showToast('Error al borrar'); }
        };
        const handleWA = () => {
          const cli = presupCliente || 'Cliente';
          const lines = ['*ESSENCE PURE* - Presupuesto para ' + cli + '\n'];
          presupCart.forEach(item => {
            const lt = Math.round(item.precio * item.qty * (1 - item.desc / 100));
            lines.push(item.prod.nombre + ' (' + tipoLabel(item.tipo) + ')');
            lines.push('   ' + item.qty + ' x ' + fmtP(item.precio) + (item.desc > 0 ? ' -' + item.desc + '%' : '') + ' = *' + fmtP(lt) + '*');
          });
          if (envioAmt > 0) lines.push('\nEnvio: ' + fmtP(envioAmt));
          lines.push('\n*Total: ' + fmtP(total) + '*');
          lines.push('\n_essencepure.carpell.com.ar_');
          window.open('https://wa.me/?text=' + encodeURIComponent(lines.join('\n')), '_blank');
        };
        const handlePrint = () => {
          const cli = presupCliente || 'Cliente';
          const ds = new Date().toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
          const rows = presupCart.map(item => {
            const lt = Math.round(item.precio * item.qty * (1 - item.desc / 100));
            const tb = tipoLabel(item.tipo);
            const imgHtml = item.prod.imagen_url
              ? '<img src="' + item.prod.imagen_url + '" style="width:40px;height:40px;object-fit:cover;border-radius:4px;border:1px solid #eee">'
              : '<div style="width:40px;height:40px;background:#f5f0e8;border-radius:4px;border:1px solid #eee"></div>';
            return '<tr>' +
              '<td style="padding:10px 8px;border-bottom:1px solid #f0ece4;vertical-align:top">' +
              '<div style="display:flex;gap:10px">' + imgHtml + '<div>' +
              '<div style="font-size:12px;font-weight:600;margin-bottom:2px">' + item.prod.nombre + '</div>' +
              '<div style="font-size:10px;color:#888">' + item.prod.marca + '</div>' +
              '<div style="font-size:10px;color:#999;font-style:italic">' + (item.prod.notas || '') + '</div>' +
              '<span style="font-size:9px;padding:1px 6px;background:#fdf8ee;border:1px solid #e8d5a0;border-radius:10px;color:#b8860b">' + tb + '</span>' +
              '</div></div></td>' +
              '<td style="text-align:center;font-size:12px;padding:10px 8px;border-bottom:1px solid #f0ece4">' + item.qty + '</td>' +
              '<td style="text-align:right;font-size:12px;padding:10px 8px;border-bottom:1px solid #f0ece4">' + fmtP(item.precio) + '</td>' +
              '<td style="text-align:right;font-size:12px;color:#b8860b;font-weight:600;padding:10px 8px;border-bottom:1px solid #f0ece4">' + fmtP(lt) + '</td>' +
              '</tr>';
          }).join('');
          const discHtml = gDiscAmt > 0
            ? '<div style="display:flex;justify-content:space-between;font-size:12px;color:#666;margin-bottom:4px"><span>Subtotal</span><span>' + fmtP(sub) + '</span></div>' +
              '<div style="display:flex;justify-content:space-between;font-size:12px;color:#e53935;margin-bottom:4px"><span>Desc. -' + presupGDesc + '%</span><span>-' + fmtP(gDiscAmt) + '</span></div>'
            : '';
          const envHtml = envioAmt > 0
            ? '<div style="display:flex;justify-content:space-between;font-size:12px;color:#666;margin-bottom:4px"><span>Envio</span><span>' + fmtP(envioAmt) + '</span></div>'
            : '';
          const html = '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Presupuesto - Essence Pure</title>' +
            '<style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Georgia,serif;padding:30px;color:#222;max-width:700px;margin:0 auto}' +
            'table{width:100%;border-collapse:collapse}th{font-size:10px;text-transform:uppercase;color:#888;border-bottom:1px solid #e8e0cc;padding:6px 8px;text-align:left;font-weight:400}</style></head><body>' +
            '<div style="text-align:center;border-bottom:2px solid #b8860b;padding-bottom:18px;margin-bottom:20px">' +
            '<div style="font-size:24px;letter-spacing:.2em">ESSENCE <span style="font-style:italic;color:#b8860b">PURE</span></div>' +
            '<div style="font-size:10px;color:#aaa;text-transform:uppercase;letter-spacing:.12em;margin-top:4px">Fragancias arabes importadas</div></div>' +
            '<div style="display:flex;justify-content:space-between;font-size:11px;color:#666;margin-bottom:16px"><span><strong>Para:</strong> ' + cli + '</span><span><strong>Fecha:</strong> ' + ds + '</span></div>' +
            '<table><thead><tr><th>Producto</th><th style="text-align:center">Cant.</th><th style="text-align:right">Precio</th><th style="text-align:right">Subtotal</th></tr></thead><tbody>' + rows + '</tbody></table>' +
            '<div style="display:flex;justify-content:flex-end;margin-top:16px"><div style="border-top:2px solid #b8860b;padding-top:12px;text-align:right;min-width:200px">' +
            discHtml + envHtml +
            '<div style="display:flex;justify-content:space-between;font-size:16px;font-weight:600"><span>Total</span><span style="color:#b8860b">' + fmtP(total) + '</span></div>' +
            '</div></div>' +
            '<div style="text-align:center;margin-top:24px;font-size:10px;color:#bbb">essencepure.carpell.com.ar - Importado directamente</div>' +
            '</body></html>';
          const w = window.open('', '_blank', 'width=720,height=900');
          if (!w) return;
          w.document.write(html);
          w.document.close();
          w.focus();
          w.print();
        };
        return (
          <div style={{ padding: '14px 16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14, alignItems: 'start' }}>
              {/* LEFT */}
              <div>
                <div style={S.card}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#b8860b', marginBottom: 10 }}>Agregar productos</div>
                  <div style={{ position: 'relative', marginBottom: 12 }}>
                    <input
                      style={S.inp}
                      placeholder="Buscar perfume o decant..."
                      value={presupSearch}
                      onChange={e => setPresupSearch(e.target.value)}
                    />
                    {presupSearch.trim() && filtrados.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e8e0cc', borderRadius: 8, zIndex: 20, maxHeight: 240, overflowY: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
                        {filtrados.slice(0, 6).map(p => (
                          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid #f5f0e8' }}>
                            {miniImg(p)}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nombre}</div>
                              <div style={{ fontSize: 11, color: '#aaa' }}>{p.marca}</div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                              <span style={{ fontSize: 12, color: '#b8860b', fontWeight: 600 }}>{fmtP(p.precio)}</span>
                              <div style={{ display: 'flex', gap: 3 }}>
                                <button style={{ ...S.btn(''), padding: '2px 8px', fontSize: 10 }} onClick={() => addItem(p, 'full')}>Completo</button>
                                {parseFloat(p.precio_decant_5ml) > 0 && <button style={{ ...S.btn(''), padding: '2px 8px', fontSize: 10 }} onClick={() => addItem(p, '5ml')}>5ml</button>}
                                {parseFloat(p.precio_decant_10ml) > 0 && <button style={{ ...S.btn(''), padding: '2px 8px', fontSize: 10 }} onClick={() => addItem(p, '10ml')}>10ml</button>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {presupCart.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px 0', color: '#ccc', fontSize: 12 }}>Busca perfumes arriba para agregar</div>
                  ) : presupCart.map(item => {
                    const lt = Math.round(item.precio * item.qty * (1 - item.desc / 100));
                    return (
                      <div key={item.key} style={{ border: '1px solid #e8e0cc', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
                          {miniImg(item.prod)}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.prod.nombre}</div>
                            <div style={{ fontSize: 11, color: '#aaa' }}>{item.prod.marca}</div>
                          </div>
                          <span style={{ fontSize: 10, background: '#fdf8ee', color: '#b8860b', border: '1px solid #e8d5a0', borderRadius: 10, padding: '2px 7px' }}>{tipoLabel(item.tipo)}</span>
                          <button onClick={() => removeItem(item.key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: 18, padding: 4, lineHeight: 1 }}>x</button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px 10px', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <button onClick={() => qtyUp(item.key, -1)} style={{ width: 24, height: 24, border: '1px solid #e8e0cc', borderRadius: 4, background: '#fafaf8', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }}>-</button>
                            <span style={{ width: 26, textAlign: 'center', fontSize: 13, fontWeight: 600 }}>{item.qty}</span>
                            <button onClick={() => qtyUp(item.key, 1)} style={{ width: 24, height: 24, border: '1px solid #e8e0cc', borderRadius: 4, background: '#fafaf8', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }}>+</button>
                          </div>
                          <div style={{ display: 'flex', gap: 3 }}>
                            {['full', '5ml', '10ml'].filter(t => t === 'full' || (t === '5ml' && parseFloat(item.prod.precio_decant_5ml) > 0) || (t === '10ml' && parseFloat(item.prod.precio_decant_10ml) > 0)).map(t => (
                              <button key={t} onClick={() => setItemTipo(item.key, t, item.prod)} style={{ padding: '2px 7px', border: '1px solid ' + (item.tipo === t ? '#b8860b' : '#e8e0cc'), borderRadius: 3, fontSize: 10, cursor: 'pointer', background: item.tipo === t ? '#fdf8ee' : '#fafaf8', color: item.tipo === t ? '#b8860b' : '#888', fontFamily: 'inherit' }}>{t === 'full' ? 'Completo' : t}</button>
                            ))}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 10, color: '#ccc' }}>$</span>
                            <input type="number" value={item.precio} onChange={e => setItemPrecio(item.key, e.target.value)} style={{ width: 85, padding: '3px 6px', border: '1px solid #e8e0cc', borderRadius: 4, fontSize: 12, fontFamily: 'inherit', textAlign: 'right', background: '#fafaf8' }} />
                          </div>
                          <span style={{ marginLeft: 'auto', fontWeight: 600, color: '#b8860b', fontSize: 13 }}>{fmtP(lt)}</span>
                        </div>
                        <div style={{ padding: '0 12px 10px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 10, color: '#ccc', textTransform: 'uppercase', letterSpacing: 0.5 }}>Desc:</span>
                          {[0, 5, 10, 15, 20, 25].map(d => (
                            <button key={d} onClick={() => setItemDesc(item.key, d)} style={{ padding: '2px 7px', border: '1px solid ' + (item.desc === d && d > 0 ? '#e53935' : '#e8e0cc'), borderRadius: 3, fontSize: 10, cursor: 'pointer', background: item.desc === d && d > 0 ? '#e53935' : '#fafaf8', color: item.desc === d && d > 0 ? '#fff' : '#888', fontFamily: 'inherit' }}>{d === 0 ? 'Sin desc.' : '-' + d + '%'}</button>
                          ))}
                        </div>
                        {item.prod.notas && <div style={{ fontSize: 11, color: '#bbb', fontStyle: 'italic', padding: '7px 12px 10px', borderTop: '1px solid #f5f0e8' }}>{item.prod.notas}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* RIGHT */}
              <div>
                <div style={{ ...S.card, marginBottom: 12 }}>
                  <div style={S.lbl}>Cliente (opcional)</div>
                  <input style={S.inp} placeholder="Ej: Maria Garcia" value={presupCliente} onChange={e => setPresupCliente(e.target.value)} />
                </div>
                <div style={S.card}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#b8860b', marginBottom: 12 }}>Resumen</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888', marginBottom: 5 }}>
                    <span>Productos</span><span style={{ fontWeight: 500 }}>{presupCart.reduce((a, i) => a + i.qty, 0)} items</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888', marginBottom: 5 }}>
                    <span>Subtotal</span><span>{fmtP(sub)}</span>
                  </div>
                  {gDiscAmt > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#e53935', marginBottom: 5 }}>
                      <span>Descuento -{presupGDesc}%</span><span>-{fmtP(gDiscAmt)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', border: '1px dashed #e8e0cc', borderRadius: 6, marginBottom: 6, marginTop: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => setPresupEnvioActivo(v => !v)}>
                      <div style={{ width: 28, height: 16, borderRadius: 10, background: presupEnvioActivo ? '#b8860b' : '#ddd', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                        <div style={{ width: 12, height: 12, borderRadius: 6, background: '#fff', position: 'absolute', top: 2, left: presupEnvioActivo ? 14 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                      </div>
                      <span style={{ fontSize: 12, color: '#888' }}>Envio</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 10, color: '#ccc' }}>$</span>
                      <input type="number" disabled={!presupEnvioActivo} value={presupEnvioMonto} onChange={e => setPresupEnvioMonto(e.target.value)} style={{ width: 75, padding: '3px 6px', border: '1px solid #e8e0cc', borderRadius: 4, fontSize: 12, fontFamily: 'inherit', textAlign: 'right', opacity: presupEnvioActivo ? 1 : 0.35, background: '#fafaf8' }} />
                    </div>
                  </div>
                  {envioAmt > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888', marginBottom: 5 }}>
                      <span>Envio</span><span>{fmtP(envioAmt)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 600, borderTop: '1px solid #e8e0cc', paddingTop: 10, marginTop: 6 }}>
                    <span>Total</span><span style={{ color: '#b8860b' }}>{fmtP(total)}</span>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <div style={S.lbl}>Descuento global</div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                      {[0, 5, 10, 15, 20].map(d => (
                        <button key={d} onClick={() => setPresupGDesc(d)} style={{ padding: '4px 9px', border: '1px solid ' + (d > 0 && presupGDesc === d ? '#e53935' : '#e8e0cc'), borderRadius: 3, fontSize: 11, cursor: 'pointer', background: d > 0 && presupGDesc === d ? '#e53935' : presupGDesc === d ? '#f5f0e8' : '#fafaf8', color: d > 0 && presupGDesc === d ? '#fff' : presupGDesc === d ? '#b8860b' : '#888', fontFamily: 'inherit' }}>{d === 0 ? 'Sin desc.' : '-' + d + '%'}</button>
                      ))}
                    </div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <button onClick={() => setPresupShowPreview(v => !v)} style={{ ...S.btn(''), width: '100%', marginBottom: 8, fontSize: 12 }}>Ver presupuesto / Imprimir</button>
                    <button onClick={handleWA} style={{ ...S.btn('gold'), width: '100%', fontSize: 12 }}>Compartir por WhatsApp</button>
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                    <button onClick={handleGuardar} style={{ ...S.btn(''), flex: 1, fontSize: 11 }}>Guardar</button>
                    <button onClick={() => { if (presupCart.length) setPresupConfirmando(v => !v); }} style={{ ...S.btn(presupConfirmando ? 'gold' : ''), flex: 1, fontSize: 11, opacity: presupCart.length === 0 ? 0.45 : 1 }}>Confirmar</button>
                  </div>
                  {presupConfirmando && (
                    <div style={{ marginTop: 8, padding: 12, background: '#fdf8ee', borderRadius: 8, border: '1px solid #e8d5a0' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#b8860b', marginBottom: 8 }}>Confirmar como venta</div>
                      <div style={{ marginBottom: 8 }}>
                        <div style={S.lbl}>Forma de pago</div>
                        <select value={presupFormaPago} onChange={e => setPresupFormaPago(e.target.value)} style={{ ...S.inp, marginTop: 4 }}>
                          {['Efectivo','Transferencia','MercadoPago','Cuenta corriente'].map(f => <option key={f}>{f}</option>)}
                        </select>
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <div style={S.lbl}>Monto pagado ($0 si es pendiente)</div>
                        <input type="number" value={presupPagado} onChange={e => setPresupPagado(e.target.value)} style={{ ...S.inp, marginTop: 4 }} placeholder="0" />
                      </div>
                      <button onClick={handleConfirmar} style={{ ...S.btn('gold'), width: '100%', fontSize: 13 }}>Crear venta</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {presupShowPreview && (
              <div style={{ marginTop: 14, border: '1px solid #e8e0cc', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid #e8e0cc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafaf8' }}>
                  <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#aaa' }}>Preview del presupuesto</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handlePrint} style={{ ...S.btn(''), padding: '5px 12px', fontSize: 11 }}>Imprimir</button>
                    <button onClick={() => setPresupShowPreview(false)} style={{ ...S.btn(''), padding: '5px 12px', fontSize: 11 }}>Cerrar</button>
                  </div>
                </div>
                <div style={{ padding: 20, background: '#f5f5f3' }}>
                  <div style={{ background: '#fff', padding: 28, maxWidth: 600, margin: '0 auto', fontFamily: 'Georgia, serif' }}>
                    <div style={{ textAlign: 'center', borderBottom: '2px solid #b8860b', paddingBottom: 18, marginBottom: 20 }}>
                      <div style={{ fontSize: 22, fontWeight: 400, letterSpacing: '0.2em', color: '#111' }}>ESSENCE <span style={{ fontStyle: 'italic', color: '#b8860b' }}>PURE</span></div>
                      <div style={{ fontSize: 10, color: '#aaa', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: 4 }}>Fragancias arabes importadas</div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#666', marginBottom: 16, fontFamily: 'sans-serif' }}>
                      <span><strong>Para:</strong> {presupCliente || 'Cliente'}</span>
                      <span><strong>Fecha:</strong> {new Date().toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    </div>
                    {presupCart.length === 0 ? (
                      <div style={{ textAlign: 'center', color: '#ccc', padding: 20, fontFamily: 'sans-serif', fontSize: 13 }}>Agregar productos para verlos aqui</div>
                    ) : (
                      <>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20, fontFamily: 'sans-serif' }}>
                          <thead>
                            <tr>
                              <th style={{ fontSize: 10, textTransform: 'uppercase', color: '#888', borderBottom: '1px solid #e8e0cc', padding: '6px 8px', textAlign: 'left', fontWeight: 400 }}>Producto</th>
                              <th style={{ fontSize: 10, textTransform: 'uppercase', color: '#888', borderBottom: '1px solid #e8e0cc', padding: '6px 8px', textAlign: 'center', fontWeight: 400 }}>Cant.</th>
                              <th style={{ fontSize: 10, textTransform: 'uppercase', color: '#888', borderBottom: '1px solid #e8e0cc', padding: '6px 8px', textAlign: 'right', fontWeight: 400 }}>Precio</th>
                              <th style={{ fontSize: 10, textTransform: 'uppercase', color: '#888', borderBottom: '1px solid #e8e0cc', padding: '6px 8px', textAlign: 'right', fontWeight: 400 }}>Subtotal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {presupCart.map(item => {
                              const lt = Math.round(item.precio * item.qty * (1 - item.desc / 100));
                              const tb = tipoLabel(item.tipo);
                              return (
                                <tr key={item.key}>
                                  <td style={{ padding: '10px 8px', borderBottom: '1px solid #f0ece4', verticalAlign: 'top' }}>
                                    <div style={{ display: 'flex', gap: 10 }}>
                                      {item.prod.imagen_url
                                        ? <img src={item.prod.imagen_url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee', flexShrink: 0 }} />
                                        : <div style={{ width: 40, height: 40, background: '#f5f0e8', borderRadius: 4, border: '1px solid #eee', flexShrink: 0 }} />
                                      }
                                      <div>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#111', marginBottom: 2 }}>{item.prod.nombre}</div>
                                        <div style={{ fontSize: 10, color: '#888', marginBottom: 3 }}>{item.prod.marca}</div>
                                        <div style={{ fontSize: 10, color: '#999', fontStyle: 'italic', lineHeight: 1.4 }}>{item.prod.notas}</div>
                                        <span style={{ fontSize: 9, padding: '1px 6px', background: '#fdf8ee', border: '1px solid #e8d5a0', borderRadius: 10, color: '#b8860b', marginTop: 3, display: 'inline-block' }}>{tb}</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td style={{ textAlign: 'center', fontSize: 12, color: '#555', padding: '10px 8px', borderBottom: '1px solid #f0ece4' }}>{item.qty}</td>
                                  <td style={{ textAlign: 'right', fontSize: 12, color: '#555', padding: '10px 8px', borderBottom: '1px solid #f0ece4' }}>
                                    {fmtP(item.precio)}
                                    {item.desc > 0 && <div style={{ fontSize: 10, color: '#e53935' }}>-{item.desc}%</div>}
                                  </td>
                                  <td style={{ textAlign: 'right', fontSize: 12, color: '#b8860b', fontWeight: 600, padding: '10px 8px', borderBottom: '1px solid #f0ece4' }}>{fmtP(lt)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <div style={{ borderTop: '2px solid #b8860b', paddingTop: 12, textAlign: 'right', minWidth: 200 }}>
                            {gDiscAmt > 0 && <>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666', marginBottom: 4, fontFamily: 'sans-serif' }}><span>Subtotal</span><span>{fmtP(sub)}</span></div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#e53935', marginBottom: 4, fontFamily: 'sans-serif' }}><span>Desc. -{presupGDesc}%</span><span>-{fmtP(gDiscAmt)}</span></div>
                            </>}
                            {envioAmt > 0 && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666', marginBottom: 4, fontFamily: 'sans-serif' }}><span>Envio</span><span>{fmtP(envioAmt)}</span></div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 600, color: '#111', fontFamily: 'sans-serif' }}><span>Total</span><span style={{ color: '#b8860b' }}>{fmtP(total)}</span></div>
                          </div>
                        </div>
                      </>
                    )}
                    <div style={{ textAlign: 'center', marginTop: 20, fontSize: 10, color: '#bbb', fontFamily: 'sans-serif', letterSpacing: '0.06em' }}>essencepure.carpell.com.ar - Importado directamente</div>
                  </div>
                </div>
              </div>
            )}
            <div style={{ marginTop: 14 }}>
              {presupList.length === 0 ? (
                <button onClick={loadPresupList} style={{ ...S.btn(''), fontSize: 12, width: '100%' }}>Ver historial de presupuestos</button>
              ) : (
                <div style={S.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#b8860b' }}>Presupuestos guardados</div>
                    <button onClick={loadPresupList} style={{ ...S.btn(''), padding: '3px 10px', fontSize: 11 }}>Recargar</button>
                  </div>
                  {presupList.filter(p => p.estado !== 'confirmado').length === 0 && (
                    <div style={{ fontSize: 12, color: '#aaa', textAlign: 'center', padding: '8px 0' }}>No hay presupuestos pendientes</div>
                  )}
                  {presupList.filter(p => p.estado !== 'confirmado').map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f5f0e8' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{p.cliente || 'Sin nombre'} <span style={{ fontSize: 11, color: '#aaa' }}>#{p.id}</span></div>
                        <div style={{ fontSize: 11, color: '#aaa' }}>{new Date(p.created_at).toLocaleDateString('es-AR')} &mdash; {fmtP(p.total)}</div>
                      </div>
                      <button onClick={() => handleCargarPresup(p)} style={{ ...S.btn(''), padding: '3px 10px', fontSize: 11 }}>Cargar</button>
                      <button onClick={() => handleBorrarPresup(p.id)} style={{ padding: '3px 8px', border: '1px solid #fcc', borderRadius: 4, background: 'none', color: '#e53935', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' }}>x</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* MODAL CONFIRMACION ELIMINAR */}
      {confirmDelete && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={() => setConfirmDelete(null)}>
          <div style={{ background:'#fff', borderRadius:14, padding:24, maxWidth:360, width:'100%', boxShadow:'0 8px 32px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:28, textAlign:'center', marginBottom:8 }}>⚠️</div>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:6, textAlign:'center' }}>¿Eliminar esta venta?</div>
            <div style={{ fontSize:13, color:'#555', marginBottom:12, textAlign:'center' }}>{confirmDelete.desc}</div>
            <div style={{ background:'#fff3e0', borderRadius:8, padding:'8px 12px', fontSize:12, color:'#e65100', marginBottom:16, lineHeight:1.5 }}>
              Esta acción no se puede deshacer. El stock de los productos será restaurado automáticamente.
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button style={{ ...S.btn(''), flex:1, padding:'10px 0' }} onClick={() => setConfirmDelete(null)}>Cancelar</button>
              <button style={{ flex:1, padding:'10px 0', background:'#e53935', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer', fontSize:14 }} onClick={() => { if(confirmDelete.action) confirmDelete.action(); else eliminarVenta(confirmDelete.id); setConfirmDelete(null); }}>Eliminar</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}